import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked, HostListener, OnDestroy, SecurityContext } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AppService } from "../app.service";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatDialog } from "@angular/material/dialog";
import { McpConfigDialogComponent } from "./mcp-config-dialog/mcp-config-dialog.component";
import { environment } from "src/environments/environment";

// Constants
const MEMORY_CONFIG = {
  API_ENDPOINTS: {
    LIST: 'memory/list',
    BY_TAG: 'memory/by-tag',
    LIST_TAG: 'memory/list-tag',
    CHANGE_TAG: 'memory/change-tag',
    SUMMARIZE: 'memory/summarize-memory',
    SUMMARIZE_EXTERNAL: 'memory/summarize-memory-external',
    DELETE: 'memory/forget',
    UPDATE_TAG: 'memory/update-tag',
    CHAT: 'memory/chat',
    CHAT_EXTERNAL: 'memory/chat-external',
    CREATE_LOCAL: 'memory/create-local',
    MEMORY_STATUS: 'memory/memory-status'
  },
  DISPLAY_MODES: {
    RAW: 'raw' as const,
    SUMMARIZED: 'summarized' as const,
    SUMMARY: 'summary' as const,
    SUMMARY_EXTERNAL: 'summary_external' as const
  },
  DEBOUNCE_TIME: 300,
  MAX_QUERY_LENGTH: 500,
  PAGE_SIZE: 20,
  MCP: {
    CONNECTOR_NAME: 'LIAM Memory',
    MCP_URL: 'https://mcp.askliam.ai/mcp',
    CLAUDE_LOGIN_URL: 'https://claude.ai/login'
  },
  CHROME: {
    EXTENSION_NAME: 'LIAM Memory Assistant',
    CHROME_STORE_URL: 'https://chromewebstore.google.com/detail/xdb-memory-assistant/okafgbefjjepmbimkhoakcmldppilikb',
    BASE_URL: 'https://web.askbuddy.ai/devspacexdb',
    OTP_ENDPOINT: 'auth/show_user_otp'
  },
  // Confidence threshold for filtering suggested tags from /chat orchestration
  SUGGESTED_TAG_CONFIDENCE_THRESHOLD: 0.5
} as const;

// Streaming presets
const STREAMING_PRESETS = {
  FAST: {
    charDelay: 10,
    wordDelay: 40,
    itemDelay: 80
  },
  NORMAL: {
    charDelay: 20,
    wordDelay: 60,
    itemDelay: 150
  },
  SLOW: {
    charDelay: 40,
    wordDelay: 100,
    itemDelay: 250
  },
  INSTANT: {
    charDelay: 0,
    wordDelay: 0,
    itemDelay: 0
  }
} as const;

type DisplayMode = typeof MEMORY_CONFIG.DISPLAY_MODES[keyof typeof MEMORY_CONFIG.DISPLAY_MODES];
type StreamingPreset = keyof typeof STREAMING_PRESETS;
type SummaryPersona = 'general' | 'professional' | 'casual' | 'detailed' | 'brief' | 'chronological' | 'thematic' | 'bullet' | 'genz' | 'coach' | 'storyteller' | 'gamer' | 'minimalist';

interface PersonaOption {
  value: SummaryPersona;
  label: string;
  icon: string;
  description: string;
}

// NEW: Orchestration step interface for fluid UX
interface OrchestrationStep {
  icon: string;
  text: string;
  status: 'pending' | 'active' | 'complete' | 'error';
  timestamp?: Date;
  stepNumber?: number;  // NEW: Current step number (1-based)
}

// NEW: Suggested token interface from /chat orchestration
interface SuggestedToken {
  token: string;
  synonyms: string[];
}

// NEW: Suggested tag interface from /chat orchestration  
interface SuggestedTag {
  tag: string;
  confidence: number;
  reason: string;
}

// NEW: Query orchestration data from /chat response
interface QueryOrchestration {
  'list-memories': {
    query: string;
    tokens: {
      high: SuggestedToken[];
      low: string[];
    };
  };
  'list-suggested-tags': {
    suggested_tags: SuggestedTag[];
  };
}

// NEW: Full chat response data interface
interface ChatResponseData {
  mode: 'PARSE';
  intent: string;
  sequence: string[];
  parameters: {
    query?: string;
    content?: string;
    tag?: string;
    user_tag?: string;
  };
  orchestration?: QueryOrchestration;
  reasoning: string;
}

interface ChatMessage {
  type: 'user' | 'assistant' | 'system';
  text: string;
  memories?: any[];
  totalMemories?: number;
  tags?: string[];
  timestamp?: Date;
  displayMode?: DisplayMode;
  page?: number;
  hasMore?: boolean;
  liked?: boolean;
  messageType?: 'info' | 'success' | 'warning' | 'error';
  isStreaming?: boolean;
  showShortcuts?: boolean;
  memorySummary?: string;
  summaryLoading?: boolean;

  // NEW: Orchestration properties for fluid UX
  orchestrationSteps?: OrchestrationStep[];
  isOrchestrating?: boolean;
  showSkeletons?: boolean;
  totalSteps?: number;      // NEW: Total expected steps
  currentStep?: number;

  // NEW: Memory creation result
  createdMemory?: {
    content: string;
    tag?: string;
    processId?: string;
  };

  isCollapsed?: boolean;
}

@Component({
  selector: "app-chat",
  standalone: false,
  templateUrl: "./chat.component.html",
  styleUrls: [
    "./chat.component.css"
  ],
})
export class ChatComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  userKey: string = "";
  query: string = "";
  queryError: string = "";
  messages: ChatMessage[] = [];
  loading: boolean = false;
  apiKey: any;
  workspaceName: any;
  showOptionsMenu: boolean = false;
  displayMode: DisplayMode = MEMORY_CONFIG.DISPLAY_MODES.RAW;
  showDisplayModeMenu: boolean = false;

  // MCP Configuration
  showMcpModal: boolean = false;
  mcpConfig = MEMORY_CONFIG.MCP;

  // Chrome Extension Configuration
  showChromeModal: boolean = false;
  chromeConfig = MEMORY_CONFIG.CHROME;

  // Beta Mode - Intent-based chat
  betaMode: boolean = false;

  // Deep Thinking Mode - Enhanced query processing with tag expansion
  deepThinkingEnabled: boolean = true;

  // Tag editing properties
  editingTag: string | null = null;
  editingTagNewName: string = '';

  // Quick Access Tags (horizontal scroll above chat input)
  quickAccessTags: string[] = [];
  quickAccessTagsLoading: boolean = false;
  showQuickAccessTags: boolean = true;

  // Memory management properties
  editingMemoryTag: string | null = null;
  editingMemoryTagValue: string = '';
  deletingMemoryId: string | null = null;

  // Sensitive Memory Management - tracks which sensitive memories have been revealed
  revealedSensitiveMemories: Set<string> = new Set();

  // Memory Queue Status
  memoryQueueCount: number = 0;
  memoryQueueStatus: {
    pending: number;
    completed: number;
    failed: number;
    breakdown: {
      NEW: number;
      PROCESSING: number;
      PROCESSED: number;
      FAILED: number;
    };
  } | null = null;
  private memoryStatusInterval: any = null;
  private memoryStatusPollingMs: number = 10000;

  // ============================================
  // Summary Persona Configuration
  // ============================================
  summaryPersona: SummaryPersona = 'general';
  showPersonaMenu: boolean = false;
  personaOptions: PersonaOption[] = [
    // Standard
    { value: 'general', label: 'General', icon: 'fa-comment-dots', description: 'Balanced, human-like responses' },
    { value: 'professional', label: 'Professional', icon: 'fa-briefcase', description: 'Formal, business-oriented tone' },
    { value: 'casual', label: 'Casual', icon: 'fa-smile', description: 'Friendly, conversational style' },
    { value: 'detailed', label: 'Detailed', icon: 'fa-list-check', description: 'Comprehensive analysis' },
    { value: 'brief', label: 'Brief', icon: 'fa-bolt', description: 'Concise, to-the-point' },
    { value: 'chronological', label: 'Timeline', icon: 'fa-clock', description: 'Organized by time' },
    { value: 'thematic', label: 'Thematic', icon: 'fa-layer-group', description: 'Grouped by topics' },
    { value: 'bullet', label: 'Bullet Points', icon: 'fa-list-ul', description: 'Key points as a list' },

    // Personas
    { value: 'genz', label: 'Gen-Z', icon: 'fa-fire', description: 'Slang, emojis, hype energy 🔥' },
    { value: 'coach', label: 'Life Coach', icon: 'fa-heart', description: 'Supportive, motivational tone' },
    { value: 'storyteller', label: 'Storyteller', icon: 'fa-book-open', description: 'Narrative, epic journey style' },
    { value: 'gamer', label: 'Gamer', icon: 'fa-gamepad', description: 'Quest log, achievements, XP 🎮' },
    { value: 'minimalist', label: 'Minimalist', icon: 'fa-spa', description: 'Zen, calm, essentials only' }
  ];

  // Streaming properties
  isStreaming: boolean = false;
  streamingEnabled: boolean = true;
  private streamingContent: string = '';
  private streamingIndex: number = 0;
  private animationFrameId: any = null;
  private streamingSpeed: number = STREAMING_PRESETS.NORMAL.charDelay;
  private currentPreset: StreamingPreset = 'NORMAL';

  // SSE streaming properties
  private currentEventSource: EventSource | null = null;

  private shouldScrollToBottom = false;
  private autoScrollEnabled = true;
  private searchSubject = new Subject<string>();
  private currentMessageIndex = 0;

  // Entity recognition patterns
  private entityPatterns = {
    person: /\b[A-Z][a-z]+ [A-Z][a-z]+\b/g,
    place: /\b(?:in|at|from) ([A-Z][a-z]+(?:\s[A-Z][a-z]+)?)\b/gi,
    organization: /\b[A-Z][a-z]+(?:\s[A-Z][a-z]+)*(?:\s(?:Inc|Corp|LLC|Ltd|University|College|Conference))?\b/g
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public appService: AppService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.userKey = params["userkey"];
    });

    // Get workspace details
    const wsJson = this.appService.get("xdbc.workspace");
    if (wsJson !== undefined && typeof wsJson === "string" && wsJson.trim() !== "" && wsJson !== "undefined") {
      try {
        const ws = JSON.parse(wsJson);
        this.apiKey = ws.apiKey;
        this.workspaceName = ws.workspaceName;
      } catch (err) {
        console.error("Invalid JSON:", err);
      }
    }

    // Load user preferences
    const savedMode = localStorage.getItem('memoryDisplayMode');
    if (savedMode === MEMORY_CONFIG.DISPLAY_MODES.RAW ||
      savedMode === MEMORY_CONFIG.DISPLAY_MODES.SUMMARIZED ||
      savedMode === MEMORY_CONFIG.DISPLAY_MODES.SUMMARY ||
      savedMode === MEMORY_CONFIG.DISPLAY_MODES.SUMMARY_EXTERNAL) {
      this.displayMode = savedMode;
    }

    const savedStreaming = localStorage.getItem('streamingEnabled');
    this.streamingEnabled = savedStreaming !== 'false';

    const savedPreset = localStorage.getItem('streamingPreset') as StreamingPreset;
    if (savedPreset && STREAMING_PRESETS[savedPreset]) {
      this.currentPreset = savedPreset;
      this.streamingSpeed = STREAMING_PRESETS[savedPreset].charDelay;
    }

    // Load beta mode preference
    const savedBetaMode = localStorage.getItem('chatBetaMode');
    this.betaMode = savedBetaMode === 'true';

    // Load deep thinking preference
    const savedDeepThinking = localStorage.getItem('deepThinkingEnabled');
    this.deepThinkingEnabled = savedDeepThinking !== 'false'; // Default to true

    // Load summary persona preference
    const savedPersona = localStorage.getItem('summaryPersona') as SummaryPersona;
    if (savedPersona && this.personaOptions.some(p => p.value === savedPersona)) {
      this.summaryPersona = savedPersona;
    }

    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(MEMORY_CONFIG.DEBOUNCE_TIME),
      distinctUntilChanged()
    ).subscribe(query => {
      this.performSearch(query);
    });

    // Load quick access tags on init
    this.loadQuickAccessTags();

    // Start polling for memory queue status
    this.loadMemoryQueueStatus();
    this.startMemoryStatusPolling();

    // Show welcome message if this is first time entering chat after login
    this.showWelcomeMessageIfNeeded();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom && this.autoScrollEnabled) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.stopStreaming();
    this.searchSubject.complete();
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.currentEventSource) {
      this.currentEventSource.close();
      this.currentEventSource = null;
    }
    // Clear memory status polling
    this.stopMemoryStatusPolling();
  }

  trackByMemoryId(index: number, memory: any): any {
    return memory.queryHash || index;
  }

  private getMemoryIdentifier(memory: any): string {
    return memory.queryHash || '';
  }

  // ============================================
  // INLINE MEMORY MANAGEMENT METHODS
  // ============================================

  isEditingMemoryTag(memory: any): boolean {
    const memoryId = this.getMemoryIdentifier(memory);
    return this.editingMemoryTag === memoryId;
  }

  startEditingMemoryTag(memory: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.loading) return;

    const memoryId = this.getMemoryIdentifier(memory);
    this.editingMemoryTag = memoryId;
    this.editingMemoryTagValue = memory.tag || '';

    setTimeout(() => {
      const input = document.querySelector('.memory-tag-edit-input') as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  cancelMemoryTagEdit(): void {
    this.editingMemoryTag = null;
    this.editingMemoryTagValue = '';
  }

  async saveMemoryTagEdit(memory: any): Promise<void> {
    if (this.loading) return;

    const newTag = this.editingMemoryTagValue.trim();
    const currentTag = memory.tag || '';

    if (!newTag) {
      this.showSystemMessage('Tag name cannot be empty', 'error');
      return;
    }

    if (newTag === currentTag) {
      this.cancelMemoryTagEdit();
      return;
    }

    this.loading = true;
    this.cancelMemoryTagEdit();

    try {
      const payload = {
        userKey: this.userKey,
        queryHash: memory.queryHash,
        newTag: newTag
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) throw new Error("Failed to sign payload");

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.UPDATE_TAG,
        this.apiKey,
        signature
      );

      if (response && response.status === "Success") {
        this.messages.forEach(message => {
          if (message.memories) {
            const mem = message.memories.find(m => m.queryHash === memory.queryHash);
            if (mem) {
              mem.tag = newTag;
            }
          }
        });

        this.showSystemMessage(`Tag updated to "${newTag}"`, 'success');
        this.refreshQuickAccessTags();
      } else {
        throw new Error(response?.message || "Failed to update tag");
      }
    } catch (error: any) {
      console.error("Memory tag update error:", error);
      this.showSystemMessage(`Failed to update tag: ${error.message || "Unknown error"}`, 'error');
    } finally {
      this.loading = false;
    }
  }

  showDeleteConfirmation(memory: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.loading) return;

    this.deletingMemoryId = this.getMemoryIdentifier(memory);
  }

  cancelDelete(): void {
    this.deletingMemoryId = null;
  }

  isShowingDeleteConfirmation(memory: any): boolean {
    return this.deletingMemoryId === this.getMemoryIdentifier(memory);
  }

  async deleteMemory(memory: any, event?: Event): Promise<void> {
    if (event) {
      event.stopPropagation();
    }
    if (this.loading) return;

    this.loading = true;
    this.cancelDelete();

    try {
      const payload = {
        userKey: this.userKey,
        queryHash: memory.queryHash
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) throw new Error("Failed to sign payload");

      const signature = new TextDecoder().decode(signatureData);

      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        'memory/forget',
        this.apiKey,
        signature
      );

      if (response && response.status === "Success") {
        this.messages.forEach(message => {
          if (message.memories) {
            const index = message.memories.findIndex(mem =>
              mem.queryHash === memory.queryHash
            );
            if (index !== -1) {
              message.memories.splice(index, 1);
              if (message.totalMemories !== undefined && message.totalMemories > 0) {
                message.totalMemories--;
              }
            }
          }
        });

        this.showSystemMessage('Memory deleted successfully', 'success');
      } else {
        throw new Error(response?.message || "Failed to delete memory");
      }
    } catch (error: any) {
      console.error("Memory delete error:", error);
      this.showSystemMessage(`Failed to delete memory: ${error.message || "Unknown error"}`, 'error');
    } finally {
      this.loading = false;
    }
  }

  // ============================================
  // LINKED MEMORIES - Search by ConversationId
  // ============================================

  hasConversationId(memory: any): boolean {
    return !!memory.conversationId;
  }

  async searchByConversationId(memory: any, event?: Event): Promise<void> {
    if (event) {
      event.stopPropagation();
    }

    if (!memory.conversationId || this.loading) return;

    // Get a truncated version of the memory text for display
    const memoryText = memory.memory || '';
    const displayText = memoryText.length > 80 ? memoryText.substring(0, 80) + '...' : memoryText;

    // Add user message to show what we're searching for
    const userMessage: ChatMessage = {
      type: 'user',
      text: `Show linked memories`,
      timestamp: new Date()
    };
    this.messages.push(userMessage);
    this.shouldScrollToBottom = true;

    this.loading = true;

    try {
      const payload = {
        userKey: this.userKey,
        conversationId: memory.conversationId
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) throw new Error("Failed to sign payload");

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.LIST,
        this.apiKey,
        signature
      );

      if (response && response.status === "Success") {
        const memories = response.data?.memories || [];
        const totalMemories = response.data?.totalActiveMemories || memories.length;

        const assistantMessage: ChatMessage = {
          type: 'assistant',
          text: displayText,
          memories: memories,
          totalMemories: totalMemories,
          displayMode: this.displayMode,
          timestamp: new Date(),
          page: 1,
          hasMore: response.data?.hasMore || false
        };

        this.messages.push(assistantMessage);
        this.shouldScrollToBottom = true;
      } else {
        throw new Error(response?.message || "Failed to fetch linked memories");
      }
    } catch (error: any) {
      console.error("Failed to fetch linked memories:", error);
      this.showSystemMessage(`Failed to load linked memories: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      this.loading = false;
    }
  }

  // ============================================
  // MCP CONFIGURATION METHODS
  // ============================================

  openMcpConfig(): void {
    this.showMcpModal = true;
    this.showOptionsMenu = false;
  }

  closeMcpConfig(): void {
    this.showMcpModal = false;
  }

  copyMcpUrl(): void {
    navigator.clipboard.writeText(this.mcpConfig.MCP_URL).then(() => {
      this.appService.addSuccessMessage('MCP URL copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
      this.appService.addErrorMessage('Failed to copy to clipboard');
    });
  }

  copyConnectorName(): void {
    navigator.clipboard.writeText(this.mcpConfig.CONNECTOR_NAME).then(() => {
      this.appService.addSuccessMessage('Connector name copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
      this.appService.addErrorMessage('Failed to copy to clipboard');
    });
  }

  openClaudeDesktop(): void {
    window.open(this.mcpConfig.CLAUDE_LOGIN_URL, '_blank');
  }

  // ============================================
  // CHROME EXTENSION CONFIGURATION METHODS
  // ============================================

  openChromeConfig(): void {
    this.showChromeModal = true;
    this.showOptionsMenu = false;
  }

  closeChromeConfig(): void {
    this.showChromeModal = false;
  }

  copyChromeBaseUrl(): void {
    navigator.clipboard.writeText(this.chromeConfig.BASE_URL).then(() => {
      this.appService.addSuccessMessage('Base URL copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
      this.appService.addErrorMessage('Failed to copy to clipboard');
    });
  }

  copyChromeEmail(): void {
    const email = this.appService.get('xdbc.emailId') || '';
    if (email) {
      navigator.clipboard.writeText(email).then(() => {
        this.appService.addSuccessMessage('Email copied to clipboard!');
      }).catch(err => {
        console.error('Failed to copy:', err);
        this.appService.addErrorMessage('Failed to copy to clipboard');
      });
    } else {
      this.appService.addErrorMessage('Email not found');
    }
  }

  async showChromeOtp(): Promise<void> {
    this.showOptionsMenu = false;

    try {
      const payload = { userKey: this.userKey };
      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) throw new Error("Failed to sign payload");

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        this.chromeConfig.OTP_ENDPOINT,
        this.apiKey,
        signature
      );

      if (response && response.status === "Success") {
        const otp = response.data?.otp;
        if (otp) {
          await navigator.clipboard.writeText(otp);

          const otpMessage: ChatMessage = {
            type: 'assistant',
            text: `Chrome Extension OTP: ${otp}\n\n✓ Copied to clipboard!\n\nUse this OTP to authenticate the Chrome extension.`,
            messageType: 'success',
            timestamp: new Date()
          };
          this.messages.push(otpMessage);
          this.shouldScrollToBottom = true;
        }
      } else {
        throw new Error(response?.message || "Failed to fetch OTP");
      }
    } catch (error: any) {
      console.error("OTP fetch error:", error);
      this.appService.addErrorMessage("Failed to fetch OTP: " + (error.message || "Unknown error"));
    }
  }

  openChromeStore(): void {
    window.open(this.chromeConfig.CHROME_STORE_URL, '_blank');
  }

  // ============================================
  // DISPLAY MODE METHODS
  // ============================================

  toggleDisplayModeMenu(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showDisplayModeMenu = !this.showDisplayModeMenu;
    this.showOptionsMenu = false;
  }

  setDisplayMode(mode: DisplayMode): void {
    this.displayMode = mode;
    localStorage.setItem('memoryDisplayMode', mode);
    this.showDisplayModeMenu = false;
  }

  getDisplayModeIcon(): string {
    switch (this.displayMode) {
      case MEMORY_CONFIG.DISPLAY_MODES.RAW:
        return 'fa-list';
      case MEMORY_CONFIG.DISPLAY_MODES.SUMMARIZED:
        return 'fa-layer-group';
      case MEMORY_CONFIG.DISPLAY_MODES.SUMMARY:
        return 'fa-file-alt';
      case MEMORY_CONFIG.DISPLAY_MODES.SUMMARY_EXTERNAL:
        return 'fa-cloud';
      default:
        return 'fa-list';
    }
  }

  getDisplayModeText(): string {
    switch (this.displayMode) {
      case MEMORY_CONFIG.DISPLAY_MODES.RAW:
        return 'Raw';
      case MEMORY_CONFIG.DISPLAY_MODES.SUMMARIZED:
        return 'Summarized';
      case MEMORY_CONFIG.DISPLAY_MODES.SUMMARY:
        return 'Summary';
      case MEMORY_CONFIG.DISPLAY_MODES.SUMMARY_EXTERNAL:
        return 'External Summary';
      default:
        return 'Raw';
    }
  }

  /**
   * Check if any summary mode is active (SUMMARY or SUMMARY_EXTERNAL)
   */
  isSummaryModeActive(): boolean {
    return this.displayMode === MEMORY_CONFIG.DISPLAY_MODES.SUMMARY ||
      this.displayMode === MEMORY_CONFIG.DISPLAY_MODES.SUMMARY_EXTERNAL;
  }

  /**
   * Get the appropriate summary endpoint based on display mode
   */
  getSummaryEndpoint(): string {
    return this.displayMode === MEMORY_CONFIG.DISPLAY_MODES.SUMMARY_EXTERNAL
      ? MEMORY_CONFIG.API_ENDPOINTS.SUMMARIZE_EXTERNAL
      : MEMORY_CONFIG.API_ENDPOINTS.SUMMARIZE;
  }


  // ============================================
  // SUMMARY PERSONA METHODS
  // ============================================

  togglePersonaMenu(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showPersonaMenu = !this.showPersonaMenu;
    this.showDisplayModeMenu = false;
    this.showOptionsMenu = false;
  }

  setPersona(persona: SummaryPersona): void {
    this.summaryPersona = persona;
    this.showPersonaMenu = false;
    localStorage.setItem('summaryPersona', persona);

    const selectedOption = this.personaOptions.find(p => p.value === persona);
    if (selectedOption) {
      this.showSystemMessage(`Summary style set to "${selectedOption.label}"`, 'info');
    }
  }

  getPersonaLabel(): string {
    const option = this.personaOptions.find(p => p.value === this.summaryPersona);
    return option ? option.label : 'General';
  }

  getPersonaIcon(): string {
    const option = this.personaOptions.find(p => p.value === this.summaryPersona);
    return option ? option.icon : 'fa-comment-dots';
  }

  // ============================================
  // BETA MODE METHODS - FLUID ORCHESTRATION
  // ============================================

  toggleBetaMode(): void {
    this.betaMode = !this.betaMode;
    localStorage.setItem('chatBetaMode', this.betaMode.toString());

    // Clarify that beta mode only affects typed queries, not click actions
    const message = this.betaMode
      ? 'Beta mode enabled - Intent-based search for typed queries (click actions remain direct)'
      : 'Beta mode disabled - Standard search for all queries';
    this.showSystemMessage(message, 'info');
  }

  toggleDeepThinking(): void {
    this.deepThinkingEnabled = !this.deepThinkingEnabled;
    localStorage.setItem('deepThinkingEnabled', this.deepThinkingEnabled.toString());

    const message = this.deepThinkingEnabled
      ? 'Deep Thinking enabled - Will expand search using related tags for better answers'
      : 'Deep Thinking disabled - Standard query processing';
    this.showSystemMessage(message, 'info');
  }

  /**
   * Execute QUERY orchestration flow
   * 
   * Flow:
   * 1. Call list-memories with suggestedTokens
   * 2. Filter suggested tags by confidence threshold  
   * 3. Call get-by-tag for high-confidence suggested tags
   * 4. Optionally expand with tags from search results (if deep thinking enabled)
   * 5. Merge and deduplicate all results
   */
  private async executeQueryOrchestration(
    message: ChatMessage,
    parameters: { query?: string },
    orchestration: QueryOrchestration,
    originalQuery: string
  ): Promise<void> {
    // Map to collect unique memories by queryHash
    const memoryMap = new Map<string, any>();
    const processedTags = new Set<string>();

    try {
      const query = parameters?.query || originalQuery;
      const listMemoriesOrch = orchestration['list-memories'];
      const suggestedTagsOrch = orchestration['list-suggested-tags'];

      // ============================================
      // STEP 1: Search memories with suggested tokens
      // ============================================
      await this.addOrchestrationStep(message, {
        icon: '🔎',
        text: `Searching with enhanced tokens...`,
        status: 'active'
      });

      const searchPayload: {
        userKey: string;
        tokens: string[];
        query: string;
        page: number;
        limit: number;
        require_llm_summary: boolean;
        suggestedTokens?: {
          high: SuggestedToken[];
          low: string[];
        };
      } = {
        userKey: this.userKey,
        tokens: [],
        query: query,
        page: 1,
        limit: MEMORY_CONFIG.PAGE_SIZE,
        require_llm_summary: false
      };

      // Include suggested tokens from orchestration
      if (listMemoriesOrch?.tokens) {
        searchPayload.suggestedTokens = listMemoriesOrch.tokens;

        // Log token info for debugging
        const highTokens = listMemoriesOrch.tokens.high?.map(t => t.token).join(', ') || 'none';
        const lowTokens = listMemoriesOrch.tokens.low?.join(', ') || 'none';
        console.log('[QueryOrch] High tokens:', highTokens);
        console.log('[QueryOrch] Low tokens:', lowTokens);
      }

      const searchSignature = await this.signPayload(searchPayload);
      const searchResponse = await this.appService.wsPostRequestWithHeaders(
        searchPayload,
        MEMORY_CONFIG.API_ENDPOINTS.LIST,
        this.apiKey,
        searchSignature
      );

      if (!searchResponse || searchResponse.status !== "Success") {
        throw new Error(searchResponse?.message || "Memory search failed");
      }

      const searchMemories = searchResponse.data?.memories || [];

      // Add search memories to map
      searchMemories.forEach((mem: any) => {
        if (mem.queryHash) {
          memoryMap.set(mem.queryHash, mem);
        }
      });

      // Update step to complete
      const searchStepIndex = (message.orchestrationSteps?.length || 1) - 1;
      message.orchestrationSteps![searchStepIndex].text = `Found ${searchMemories.length} memories from search`;
      message.orchestrationSteps![searchStepIndex].status = 'complete';
      this.messages = [...this.messages];

      // ============================================
      // STEP 2: Collect tags from search results
      // ============================================
      searchMemories.forEach((mem: any) => {
        if (mem.notesKey && !processedTags.has(mem.notesKey)) {
          processedTags.add(mem.notesKey);
        }
        if (mem.tag && !processedTags.has(mem.tag)) {
          processedTags.add(mem.tag);
        }
        if (mem.tags && Array.isArray(mem.tags)) {
          mem.tags.forEach((tag: string) => {
            if (tag && !processedTags.has(tag)) {
              processedTags.add(tag);
            }
          });
        }
      });

      // ============================================
      // STEP 3: Filter and process suggested tags
      // ============================================
      const suggestedTags = suggestedTagsOrch?.suggested_tags || [];
      const highConfidenceTags = suggestedTags.filter(
        st => st.confidence >= MEMORY_CONFIG.SUGGESTED_TAG_CONFIDENCE_THRESHOLD
      );

      console.log('[QueryOrch] Suggested tags:', suggestedTags);
      console.log('[QueryOrch] High confidence tags (>= ' + MEMORY_CONFIG.SUGGESTED_TAG_CONFIDENCE_THRESHOLD + '):',
        highConfidenceTags.map(t => `${t.tag} (${t.confidence})`));

      // ============================================
      // STEP 4: Fetch memories by suggested tags
      // ============================================
      if (highConfidenceTags.length > 0) {
        await this.addOrchestrationStep(message, {
          icon: '🏷️',
          text: `Expanding with ${highConfidenceTags.length} suggested ${highConfidenceTags.length === 1 ? 'tag' : 'tags'}: ${highConfidenceTags.map(t => t.tag).join(', ')}`,
          status: 'active'
        });

        let additionalMemoriesCount = 0;

        for (const suggestedTag of highConfidenceTags) {
          try {
            const tagPayload = {
              userKey: this.userKey,
              tag: suggestedTag.tag,
              page: 1,
              limit: MEMORY_CONFIG.PAGE_SIZE
            };

            const tagSignature = await this.signPayload(tagPayload);
            const tagResponse = await this.appService.wsPostRequestWithHeaders(
              tagPayload,
              MEMORY_CONFIG.API_ENDPOINTS.BY_TAG,
              this.apiKey,
              tagSignature
            );

            if (tagResponse?.status === "Success" && tagResponse.data?.memories) {
              tagResponse.data.memories.forEach((mem: any) => {
                if (mem.queryHash && !memoryMap.has(mem.queryHash)) {
                  memoryMap.set(mem.queryHash, mem);
                  additionalMemoriesCount++;
                }
              });
            }
          } catch (tagError) {
            console.warn(`[QueryOrch] Failed to fetch memories for suggested tag "${suggestedTag.tag}":`, tagError);
          }
        }

        // Update the tag expansion step
        const tagStepIndex = (message.orchestrationSteps?.length || 1) - 1;
        message.orchestrationSteps![tagStepIndex].text = `Found ${additionalMemoriesCount} additional memories from suggested tags`;
        message.orchestrationSteps![tagStepIndex].status = 'complete';
        this.messages = [...this.messages];
      }

      // ============================================
      // STEP 5: Expand with tags from search results (Deep Thinking behavior)
      // ============================================
      const memoryTags = Array.from(processedTags);
      if (memoryTags.length > 0 && this.deepThinkingEnabled) {
        await this.addOrchestrationStep(message, {
          icon: '🔄',
          text: `Expanding with ${memoryTags.length} related ${memoryTags.length === 1 ? 'tag' : 'tags'} from results...`,
          status: 'active'
        });

        let relatedMemoriesCount = 0;

        for (const tag of memoryTags) {
          // Skip tags we already fetched via suggested tags
          if (highConfidenceTags.some(st => st.tag === tag)) {
            continue;
          }

          try {
            const tagPayload = {
              userKey: this.userKey,
              tag: tag,
              page: 1,
              limit: MEMORY_CONFIG.PAGE_SIZE
            };

            const tagSignature = await this.signPayload(tagPayload);
            const tagResponse = await this.appService.wsPostRequestWithHeaders(
              tagPayload,
              MEMORY_CONFIG.API_ENDPOINTS.BY_TAG,
              this.apiKey,
              tagSignature
            );

            if (tagResponse?.status === "Success" && tagResponse.data?.memories) {
              tagResponse.data.memories.forEach((mem: any) => {
                if (mem.queryHash && !memoryMap.has(mem.queryHash)) {
                  memoryMap.set(mem.queryHash, mem);
                  relatedMemoriesCount++;
                }
              });
            }
          } catch (tagError) {
            console.warn(`[QueryOrch] Failed to fetch memories for related tag "${tag}":`, tagError);
          }
        }

        // Update the related tags step
        const relatedStepIndex = (message.orchestrationSteps?.length || 1) - 1;
        message.orchestrationSteps![relatedStepIndex].text = `Found ${relatedMemoriesCount} new memories from related tags`;
        message.orchestrationSteps![relatedStepIndex].status = 'complete';
        this.messages = [...this.messages];
      }

      // ============================================
      // STEP 6: Prepare final deduplicated results
      // ============================================
      const allMemories = Array.from(memoryMap.values());

      await this.addOrchestrationStep(message, {
        icon: '📊',
        text: `Preparing ${allMemories.length} unique memories...`,
        status: 'active'
      });

      await this.delay(200);

      // Transform message to show results
      message.isOrchestrating = false;
      message.showSkeletons = false;
      message.text = query;
      message.totalMemories = allMemories.length;
      message.displayMode = this.displayMode;
      message.page = 1;
      message.hasMore = false; // We've collected all relevant memories
      message.memories = [];
      message.summaryLoading = this.isSummaryModeActive() && allMemories.length > 0;

      // Update final step with result count
      if (message.orchestrationSteps?.length) {
        const lastStep = message.orchestrationSteps[message.orchestrationSteps.length - 1];
        lastStep.text = `${allMemories.length} unique ${allMemories.length === 1 ? 'memory' : 'memories'} ready`;
        lastStep.status = 'complete';
      }

      this.messages = [...this.messages];
      this.shouldScrollToBottom = true;

      // Stream memories one by one
      if (allMemories.length > 0) {
        await this.delay(300);
        this.streamMemories(allMemories, message, () => {
          this.messages = [...this.messages];
        });
      }

      // Generate comprehensive summary if in summary mode
      if (this.isSummaryModeActive() && allMemories.length > 0) {
        this.fetchMemorySummaryStream(message, allMemories, query);
      }

    } catch (error: any) {
      console.error("[QueryOrch] Orchestration error:", error);

      // Update last step to error
      if (message.orchestrationSteps?.length) {
        const lastStep = message.orchestrationSteps.length - 1;
        await this.updateOrchestrationStep(message, lastStep, 'error');
      }

      // Add error step
      await this.addOrchestrationStep(message, {
        icon: '⚠️',
        text: `Error: ${error.message || 'Unknown error'}`,
        status: 'error'
      });

      message.isOrchestrating = false;
      message.showSkeletons = false;
      message.messageType = 'error';
      message.text = `Failed to process query: ${error.message || 'Unknown error'}`;
      this.messages = [...this.messages];
    }
  }

  /**
 * UPDATED: performBetaSearch with step counting
 * Replace the existing method (around line 1135)
 */
  private async performBetaSearch(searchQuery: string): Promise<void> {
    // Create a single message that will transform through stages
    const orchestrationMessage: ChatMessage = {
      type: 'assistant',
      text: '',
      timestamp: new Date(),
      orchestrationSteps: [],
      isOrchestrating: true,
      showSkeletons: false,
      totalSteps: 8, // Initial estimate, will be refined after intent analysis
      currentStep: 0
    };

    this.messages.push(orchestrationMessage);
    this.shouldScrollToBottom = true;

    try {
      // STEP 1: Fetch existing tags first
      await this.addOrchestrationStep(orchestrationMessage, {
        icon: '🏷️',
        text: 'Loading your tags...',
        status: 'active'
      });

      // Fetch existing tags
      const existingTags = await this.fetchExistingTagsForChat();

      // Update step to complete
      await this.updateOrchestrationStep(orchestrationMessage, 0, 'complete');

      // STEP 2: Understanding request
      await this.addOrchestrationStep(orchestrationMessage, {
        icon: '🔍',
        text: 'Understanding your request...',
        status: 'active'
      });

      // Build chat payload with existing_tags
      const payload = {
        userKey: this.userKey,
        statement: searchQuery,
        existing_tags: existingTags
      };

      console.log('[Beta] Calling /chat with existing_tags:', existingTags);

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) {
        throw new Error("Failed to sign payload");
      }

      const signature = new TextDecoder().decode(signatureData);

      // STEP 3: Analyzing intent
      await this.updateOrchestrationStep(orchestrationMessage, 1, 'complete');
      await this.addOrchestrationStep(orchestrationMessage, {
        icon: '🧠',
        text: 'Analyzing intent...',
        status: 'active'
      });

      // Select chat endpoint based on display mode
      const chatEndpoint = this.displayMode === MEMORY_CONFIG.DISPLAY_MODES.SUMMARY_EXTERNAL
        ? MEMORY_CONFIG.API_ENDPOINTS.CHAT_EXTERNAL
        : MEMORY_CONFIG.API_ENDPOINTS.CHAT;

      console.log('[Beta] Using chat endpoint:', chatEndpoint, 'for displayMode:', this.displayMode);

      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        chatEndpoint,
        this.apiKey,
        signature
      );

      if (response && response.status === "Success") {
        const intentData: ChatResponseData = response.data;
        const intent = intentData?.intent;
        const sequence = intentData?.sequence || [];
        const parameters = intentData?.parameters || {};
        const orchestration = intentData?.orchestration;
        const reasoning = intentData?.reasoning || '';

        console.log('[Beta] Intent detected:', intent, 'Sequence:', sequence, 'Parameters:', parameters);
        console.log('[Beta] Orchestration:', orchestration);

        // NEW: Calculate and update total steps based on intent analysis
        orchestrationMessage.totalSteps = this.calculateTotalSteps(intent, sequence, orchestration);
        console.log('[Beta] Total steps calculated:', orchestrationMessage.totalSteps);

        // STEP 4: Show what was detected
        await this.updateOrchestrationStep(orchestrationMessage, 2, 'complete');
        await this.addOrchestrationStep(orchestrationMessage, {
          icon: '✨',
          text: this.getIntentDescription(intent, parameters),
          status: 'active'
        });

        // Show skeleton loaders while fetching actual data
        orchestrationMessage.showSkeletons = true;
        this.messages = [...this.messages];

        // Brief pause to show the detected intent
        await this.delay(400);

        // STEP 5: Complete orchestration and fetch results
        await this.updateOrchestrationStep(orchestrationMessage, 3, 'complete');

        // Check if this is a QUERY intent with orchestration data
        if (intent === 'QUERY' && orchestration) {
          await this.executeQueryOrchestration(orchestrationMessage, parameters, orchestration, searchQuery);
        } else {
          // Execute the intent using existing flow
          await this.executeIntentWithStreaming(orchestrationMessage, intent, sequence, parameters, searchQuery);
        }

      } else {
        throw new Error(response?.message || "Intent analysis failed");
      }
    } catch (error: any) {
      console.error("Beta search error:", error);

      // Update orchestration to show error state
      if (orchestrationMessage.orchestrationSteps?.length) {
        const lastStep = orchestrationMessage.orchestrationSteps.length - 1;
        await this.updateOrchestrationStep(orchestrationMessage, lastStep, 'error');
      }

      // Add error step
      await this.addOrchestrationStep(orchestrationMessage, {
        icon: '⚠️',
        text: 'Falling back to standard search...',
        status: 'active'
      });

      orchestrationMessage.isOrchestrating = false;
      orchestrationMessage.showSkeletons = false;
      this.messages = [...this.messages];

      // Fallback to standard search
      await this.delay(800);

      // Remove the orchestration message and do standard search
      const idx = this.messages.indexOf(orchestrationMessage);
      if (idx > -1) {
        this.messages.splice(idx, 1);
      }

      await this.performSearch(searchQuery);
    } finally {
      this.loading = false;
    }
  }

  /**
   * NEW: Fetch existing tags for chat intent discovery
   * Returns array of tag names to pass to /chat endpoint
   */
  private async fetchExistingTagsForChat(): Promise<string[]> {
    try {
      const payload = {
        userKey: this.userKey
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) {
        console.warn('[Beta] Failed to sign tags payload, continuing without tags');
        return [];
      }

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.LIST_TAG,
        this.apiKey,
        signature
      );

      if (response?.status === "Success") {
        const tags = response.data?.notesTag || [];
        console.log('[Beta] Fetched existing tags:', tags.length);

        // Also update quickAccessTags for UI consistency
        this.quickAccessTags = tags;

        return tags;
      }

      console.warn('[Beta] Tags fetch returned non-success, continuing without tags');
      return [];
    } catch (error) {
      console.warn('[Beta] Failed to fetch tags for chat:', error);
      return [];
    }
  }

  /**
  * Calculate total expected steps based on intent and orchestration data
  * This gives users transparency about why the operation takes time
  */
  private calculateTotalSteps(intent: string, sequence: string[], orchestration?: QueryOrchestration): number {
    // Base steps that always happen:
    // 1. Loading tags
    // 2. Understanding request  
    // 3. Analyzing intent
    // 4. Intent description (Searching for "X"...)
    let totalSteps = 4;

    switch (intent) {
      case 'QUERY':
        // +1 for search with enhanced tokens
        totalSteps += 1;

        // Check for suggested tags with high confidence
        if (orchestration?.['list-suggested-tags']?.suggested_tags) {
          const highConfidenceTags = orchestration['list-suggested-tags'].suggested_tags.filter(
            st => st.confidence >= MEMORY_CONFIG.SUGGESTED_TAG_CONFIDENCE_THRESHOLD
          );
          if (highConfidenceTags.length > 0) {
            totalSteps += 1; // Expanding with suggested tags
          }
        }

        // +1 for related tags expansion (if deep thinking enabled)
        if (this.deepThinkingEnabled) {
          totalSteps += 1; // Expanding with related tags
        }

        // +1 for preparing results
        totalSteps += 1;
        break;

      case 'CREATE':
        // +1 for fetching available tags
        // +1 for creating memory
        // +1 for success confirmation
        totalSteps += 3;
        break;

      case 'list-memories':
        if (this.deepThinkingEnabled) {
          // Deep thinking adds more steps
          totalSteps += 4; // Search + Tags found + Expanding + Preparing
        } else {
          totalSteps += 1; // Just search
        }
        break;

      case 'list-tags':
      case 'TAG_LIST':
        totalSteps += 1; // Fetching tags
        break;

      case 'get-by-tag':
      case 'TAG_QUERY':
        totalSteps += 1; // Fetching memories by tag
        break;

      default:
        totalSteps += 1; // Default search
        break;
    }

    return totalSteps;
  }

  /**
   * UPDATED: Helper to add an orchestration step with step number tracking
   */
  private async addOrchestrationStep(
    message: ChatMessage,
    step: OrchestrationStep
  ): Promise<void> {
    step.timestamp = new Date();

    // Calculate step number
    const currentStepNum = (message.orchestrationSteps?.length || 0) + 1;
    step.stepNumber = currentStepNum;
    message.currentStep = currentStepNum;

    message.orchestrationSteps = [...(message.orchestrationSteps || []), step];
    this.messages = [...this.messages];
    this.shouldScrollToBottom = true;
    await this.delay(300); // Brief pause for visual effect
  }

  /**
   * Helper: Update an orchestration step's status
   */
  private async updateOrchestrationStep(
    message: ChatMessage,
    stepIndex: number,
    status: OrchestrationStep['status']
  ): Promise<void> {
    if (message.orchestrationSteps?.[stepIndex]) {
      message.orchestrationSteps[stepIndex].status = status;
      this.messages = [...this.messages];
      await this.delay(150);
    }
  }

  /**
   * Helper: Get human-readable description of detected intent
   */
  private getIntentDescription(intent: string, parameters: any): string {
    switch (intent) {
      case 'list-memories':
        return parameters?.query
          ? `Searching for "${parameters.query}"...`
          : 'Loading all memories...';
      case 'list-tags':
      case 'TAG_LIST':
        return 'Loading your tags...';
      case 'get-by-tag':
      case 'TAG_QUERY':
        return `Finding memories tagged "${parameters?.tag || parameters?.query}"...`;
      case 'QUERY':
        return parameters?.query
          ? `Searching for "${parameters.query}"...`
          : 'Searching memories...';
      case 'CREATE':
        return 'Creating new memory...';
      default:
        return 'Processing your request...';
    }
  }

  /**
   * Helper: Promise-based delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Execute intent and stream results into the orchestration message
   */
  private async executeIntentWithStreaming(
    message: ChatMessage,
    intent: string,
    sequence: string[],
    parameters: any,
    originalQuery: string
  ): Promise<void> {
    const action = sequence[0] || intent;

    switch (action) {
      case 'list-memories':
      case 'QUERY':
        const query = parameters?.query || originalQuery;
        if (parameters?.query === '' || query === 'show my memories' || query === 'list all memories' || !query) {
          await this.fetchAndStreamMemories(message, '');
        } else {
          // Use deep thinking mode for queries if enabled
          if (this.deepThinkingEnabled) {
            await this.executeDeepThinkingQuery(message, query);
          } else {
            await this.fetchAndStreamMemories(message, query);
          }
        }
        break;

      case 'list-tags':
      case 'TAG_LIST':
        // Check if this is part of CREATE sequence
        if (intent === 'CREATE' && sequence.includes('create-memory')) {
          await this.executeCreateMemorySequence(message, sequence, parameters, originalQuery);
        } else {
          await this.fetchAndStreamTags(message);
        }
        break;

      case 'get-by-tag':
      case 'TAG_QUERY':
        const tag = parameters?.tag || parameters?.query;
        if (tag) {
          await this.fetchAndStreamMemoriesByTag(message, tag);
        } else {
          await this.fetchAndStreamMemories(message, originalQuery);
        }
        break;

      case 'CREATE':
      case 'create-memory':
        await this.executeCreateMemorySequence(message, sequence, parameters, originalQuery);
        break;

      default:
        console.log('[Beta] Unknown intent/action:', action, '- falling back to search');
        await this.fetchAndStreamMemories(message, originalQuery);
        break;
    }
  }

  /**
   * DEEP THINKING MODE: Enhanced query processing
   * 1. Search memories by query
   * 2. Extract unique tags from found memories
   * 3. Fetch additional memories by each tag
   * 4. Deduplicate all memories using a Set
   * 5. Generate comprehensive summary
   * 
   * Note: This is called from executeIntentWithStreaming after the beta flow
   * has already shown "Understanding", "Analyzing", and "Searching" steps.
   * We should NOT add another "Searching" step.
   */
  private async executeDeepThinkingQuery(message: ChatMessage, query: string): Promise<void> {
    // Map to collect unique memories by queryHash
    const memoryMap = new Map<string, any>();
    const processedTags = new Set<string>();

    try {
      // The beta flow already showed "Searching for X...", so we just do the search
      // without adding another orchestration step for it

      const initialPayload = {
        userKey: this.userKey,
        tokens: [],
        query: query,
        page: 1,
        limit: MEMORY_CONFIG.PAGE_SIZE,
        require_llm_summary: false
      };

      const initialSignature = await this.signPayload(initialPayload);
      const initialResponse = await this.appService.wsPostRequestWithHeaders(
        initialPayload,
        MEMORY_CONFIG.API_ENDPOINTS.LIST,
        this.apiKey,
        initialSignature
      );

      if (!initialResponse || initialResponse.status !== "Success") {
        throw new Error(initialResponse?.message || "Initial search failed");
      }

      const initialMemories = initialResponse.data?.memories || [];

      // Add initial memories to map
      initialMemories.forEach((mem: any) => {
        if (mem.queryHash) {
          memoryMap.set(mem.queryHash, mem);
        }
      });

      // Update the last step (which was "Searching...") to show results
      if (message.orchestrationSteps?.length) {
        const lastStep = message.orchestrationSteps[message.orchestrationSteps.length - 1];
        lastStep.text = `Found ${initialMemories.length} memories from search`;
        lastStep.status = 'complete';
        this.messages = [...this.messages];
      }

      // STEP 2: Extract unique tags from memories
      // Note: The tag property is called 'notesKey' in the memory object
      const uniqueTags: string[] = [];
      initialMemories.forEach((mem: any) => {
        // Primary tag field is 'notesKey'
        if (mem.notesKey && !processedTags.has(mem.notesKey)) {
          uniqueTags.push(mem.notesKey);
          processedTags.add(mem.notesKey);
        }
        // Fallback: check 'tag' property
        if (mem.tag && !processedTags.has(mem.tag)) {
          uniqueTags.push(mem.tag);
          processedTags.add(mem.tag);
        }
        // Also check for tags array if present
        if (mem.tags && Array.isArray(mem.tags)) {
          mem.tags.forEach((tag: string) => {
            if (tag && !processedTags.has(tag)) {
              uniqueTags.push(tag);
              processedTags.add(tag);
            }
          });
        }
      });

      // STEP 3: If tags found, fetch memories by each tag
      if (uniqueTags.length > 0) {
        await this.addOrchestrationStep(message, {
          icon: '🏷️',
          text: `Found ${uniqueTags.length} related ${uniqueTags.length === 1 ? 'tag' : 'tags'}: ${uniqueTags.slice(0, 3).join(', ')}${uniqueTags.length > 3 ? '...' : ''}`,
          status: 'active'
        });

        await this.delay(300);

        // Update to complete
        const tagStepIndex = (message.orchestrationSteps?.length || 1) - 1;
        await this.updateOrchestrationStep(message, tagStepIndex, 'complete');

        // Fetch memories for each tag
        await this.addOrchestrationStep(message, {
          icon: '🔄',
          text: `Expanding search with related tags...`,
          status: 'active'
        });

        let additionalMemoriesCount = 0;

        for (const tag of uniqueTags) {
          try {
            const tagPayload = {
              userKey: this.userKey,
              tag: tag,
              page: 1,
              limit: MEMORY_CONFIG.PAGE_SIZE
            };

            const tagSignature = await this.signPayload(tagPayload);
            const tagResponse = await this.appService.wsPostRequestWithHeaders(
              tagPayload,
              MEMORY_CONFIG.API_ENDPOINTS.BY_TAG,
              this.apiKey,
              tagSignature
            );

            if (tagResponse?.status === "Success" && tagResponse.data?.memories) {
              tagResponse.data.memories.forEach((mem: any) => {
                if (mem.queryHash && !memoryMap.has(mem.queryHash)) {
                  memoryMap.set(mem.queryHash, mem);
                  additionalMemoriesCount++;
                }
              });
            }
          } catch (tagError) {
            console.warn(`[DeepThinking] Failed to fetch memories for tag "${tag}":`, tagError);
          }
        }

        // Update expansion step
        const expandStepIndex = (message.orchestrationSteps?.length || 1) - 1;
        await this.updateOrchestrationStep(message, expandStepIndex, 'complete');

        if (additionalMemoriesCount > 0) {
          await this.addOrchestrationStep(message, {
            icon: '✓',
            text: `Found ${additionalMemoriesCount} additional related memories`,
            status: 'complete'
          });
        }
      }

      // STEP 4: Prepare final memory list
      const allMemories = Array.from(memoryMap.values());

      await this.addOrchestrationStep(message, {
        icon: '📊',
        text: `Preparing ${allMemories.length} total unique memories...`,
        status: 'active'
      });

      await this.delay(200);

      // Transform message to show results
      message.isOrchestrating = false;
      message.showSkeletons = false;
      message.text = query;
      message.totalMemories = allMemories.length;
      message.displayMode = this.displayMode;
      message.page = 1;
      message.hasMore = false; // We've collected all relevant memories
      message.memories = [];
      message.summaryLoading = this.isSummaryModeActive() && allMemories.length > 0;

      // Update final step with result count
      if (message.orchestrationSteps?.length) {
        const lastStep = message.orchestrationSteps[message.orchestrationSteps.length - 1];
        lastStep.text = `${allMemories.length} unique ${allMemories.length === 1 ? 'memory' : 'memories'} ready`;
        lastStep.status = 'complete';
      }

      this.messages = [...this.messages];
      this.shouldScrollToBottom = true;

      // Stream memories one by one
      if (allMemories.length > 0) {
        await this.delay(300);
        this.streamMemories(allMemories, message, () => {
          this.messages = [...this.messages];
        });
      }

      // STEP 5: Generate comprehensive summary if in summary mode
      if (this.isSummaryModeActive() && allMemories.length > 0) {
        this.fetchMemorySummaryStream(message, allMemories, query);
      }

    } catch (error: any) {
      console.error("[DeepThinking] Query error:", error);

      // Update last step to error
      if (message.orchestrationSteps?.length) {
        const lastStep = message.orchestrationSteps.length - 1;
        await this.updateOrchestrationStep(message, lastStep, 'error');
      }

      // Add error step
      await this.addOrchestrationStep(message, {
        icon: '⚠️',
        text: `Error: ${error.message || 'Unknown error'}`,
        status: 'error'
      });

      message.isOrchestrating = false;
      message.showSkeletons = false;
      message.messageType = 'error';
      message.text = `Failed to process query: ${error.message || 'Unknown error'}`;
      this.messages = [...this.messages];
    }
  }

  /**
   * Helper: Sign a payload and return the signature string
   */
  private async signPayload(payload: any): Promise<string> {
    const signatureData = await this.appService.wssign(payload, this.workspaceName);
    if (!signatureData) {
      throw new Error("Failed to sign payload");
    }
    return new TextDecoder().decode(signatureData);
  }

  /**
   * Execute the CREATE memory sequence with transparent orchestration
   * Sequence: list-tags -> create-memory
   */
  private async executeCreateMemorySequence(
    message: ChatMessage,
    sequence: string[],
    parameters: any,
    originalQuery: string
  ): Promise<void> {
    const content = parameters?.content || originalQuery;
    const userTag = parameters?.user_tag || null;

    try {
      // Step: Fetching available tags
      await this.addOrchestrationStep(message, {
        icon: '🏷️',
        text: 'Fetching available tags...',
        status: 'active'
      });

      // Fetch existing tags
      const tagsPayload = {
        userKey: this.userKey
      };

      const tagsSignatureData = await this.appService.wssign(tagsPayload, this.workspaceName);
      if (!tagsSignatureData) throw new Error("Failed to sign tags payload");

      const tagsSignature = new TextDecoder().decode(tagsSignatureData);
      const tagsResponse = await this.appService.wsPostRequestWithHeaders(
        tagsPayload,
        MEMORY_CONFIG.API_ENDPOINTS.LIST_TAG,
        this.apiKey,
        tagsSignature
      );

      let existingTags: string[] = [];
      if (tagsResponse?.status === "Success") {
        existingTags = tagsResponse.data?.notesTag || [];
      }

      // Update step to complete
      const lastStepIndex = (message.orchestrationSteps?.length || 1) - 1;
      await this.updateOrchestrationStep(message, lastStepIndex, 'complete');

      // Step: Creating memory
      await this.addOrchestrationStep(message, {
        icon: '💾',
        text: `Saving memory: "${content.length > 50 ? content.substring(0, 50) + '...' : content}"`,
        status: 'active'
      });

      // Call create-local endpoint
      const createPayload = {
        userKey: this.userKey,
        existing_tags: existingTags,
        content: content
      };

      const createSignatureData = await this.appService.wssign(createPayload, this.workspaceName);
      if (!createSignatureData) throw new Error("Failed to sign create payload");

      const createSignature = new TextDecoder().decode(createSignatureData);
      const createResponse = await this.appService.wsPostRequestWithHeaders(
        createPayload,
        MEMORY_CONFIG.API_ENDPOINTS.CREATE_LOCAL,
        this.apiKey,
        createSignature
      );

      if (createResponse?.status === "Success") {
        // Update to complete
        const createStepIndex = (message.orchestrationSteps?.length || 1) - 1;
        await this.updateOrchestrationStep(message, createStepIndex, 'complete');

        // Add success step
        await this.addOrchestrationStep(message, {
          icon: '✅',
          text: 'Memory added to processing queue',
          status: 'complete'
        });

        // Transform message to show success
        message.isOrchestrating = false;
        message.showSkeletons = false;
        message.messageType = 'success';
        message.text = 'Memory created successfully!';
        message.createdMemory = {
          content: content,
          processId: createResponse.processId || createResponse.referenceId
        };

        this.messages = [...this.messages];
        this.shouldScrollToBottom = true;

        // Refresh tags in case a new one was created
        this.refreshQuickAccessTags();

      } else {
        throw new Error(createResponse?.message || "Failed to create memory");
      }

    } catch (error: any) {
      console.error("Create memory error:", error);

      // Update last step to error
      if (message.orchestrationSteps?.length) {
        const lastStep = message.orchestrationSteps.length - 1;
        await this.updateOrchestrationStep(message, lastStep, 'error');
      }

      // Add error step
      await this.addOrchestrationStep(message, {
        icon: '❌',
        text: `Failed: ${error.message || 'Unknown error'}`,
        status: 'error'
      });

      message.isOrchestrating = false;
      message.showSkeletons = false;
      message.messageType = 'error';
      message.text = `Failed to create memory: ${error.message || 'Unknown error'}`;
      this.messages = [...this.messages];
    }
  }

  /**
   * Fetch memories and stream them into the orchestration message
   */
  private async fetchAndStreamMemories(message: ChatMessage, query: string): Promise<void> {
    try {
      const payload = {
        userKey: this.userKey,
        tokens: [],
        query: query,
        page: 1,
        limit: MEMORY_CONFIG.PAGE_SIZE,
        require_llm_summary: this.isSummaryModeActive()
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) throw new Error("Failed to sign payload");

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.LIST,
        this.apiKey,
        signature
      );

      if (response?.status === "Success") {
        // Transform message from orchestration to results
        message.isOrchestrating = false;
        message.showSkeletons = false;
        message.text = query || 'all memories';
        message.totalMemories = response.data?.totalActiveMemories || 0;
        message.displayMode = this.displayMode;
        message.page = 1;
        message.hasMore = response.data?.hasMore || false;
        message.memories = [];
        message.summaryLoading = this.isSummaryModeActive() && (response.data?.memories?.length > 0);

        // Update final step with result count
        if (message.orchestrationSteps?.length) {
          const lastStep = message.orchestrationSteps[message.orchestrationSteps.length - 1];
          lastStep.text = `Found ${message.totalMemories} ${message.totalMemories === 1 ? 'memory' : 'memories'}`;
          lastStep.status = 'complete';
        }

        this.messages = [...this.messages];
        this.shouldScrollToBottom = true;

        // Stream memories in one by one
        if (response.data?.memories?.length > 0) {
          await this.delay(300);
          this.streamMemories(response.data.memories, message, () => {
            this.messages = [...this.messages];
          });
        }

        // Fetch LLM summary if in summary mode
        if (this.isSummaryModeActive() && response.data?.memories?.length > 0) {
          this.fetchMemorySummaryStream(message, response.data.memories, query);
        }
      } else {
        throw new Error(response?.message || "Search failed");
      }
    } catch (error: any) {
      console.error("Fetch memories error:", error);
      message.isOrchestrating = false;
      message.showSkeletons = false;
      message.messageType = 'error';
      message.text = `Failed to load memories: ${error.message || 'Unknown error'}`;
      this.messages = [...this.messages];
    }
  }

  /**
   * Fetch tags and stream them into the orchestration message
   */
  private async fetchAndStreamTags(message: ChatMessage): Promise<void> {
    try {
      const payload = {
        userKey: this.userKey
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) throw new Error("Failed to sign payload");

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.LIST_TAG,
        this.apiKey,
        signature
      );

      if (response?.status === "Success") {
        // Transform message from orchestration to results
        message.isOrchestrating = false;
        message.showSkeletons = false;
        message.text = 'all tags';
        message.tags = response.data?.notesTag || [];

        // Update final step with result count
        if (message.orchestrationSteps?.length) {
          const lastStep = message.orchestrationSteps[message.orchestrationSteps.length - 1];
          const tagCount = message.tags?.length || 0;
          lastStep.text = `Found ${tagCount} ${tagCount === 1 ? 'tag' : 'tags'}`;
          lastStep.status = 'complete';
        }

        this.messages = [...this.messages];
        this.shouldScrollToBottom = true;
      } else {
        throw new Error(response?.message || "Failed to fetch tags");
      }
    } catch (error: any) {
      console.error("Fetch tags error:", error);
      message.isOrchestrating = false;
      message.showSkeletons = false;
      message.messageType = 'error';
      message.text = `Failed to load tags: ${error.message || 'Unknown error'}`;
      this.messages = [...this.messages];
    }
  }

  /**
   * Fetch memories by tag and stream them into the orchestration message
   */
  private async fetchAndStreamMemoriesByTag(message: ChatMessage, tag: string): Promise<void> {
    try {
      const payload = {
        userKey: this.userKey,
        tag: tag,
        page: 1,
        limit: MEMORY_CONFIG.PAGE_SIZE
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) throw new Error("Failed to sign payload");

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.BY_TAG,
        this.apiKey,
        signature
      );

      if (response?.status === "Success") {
        // Transform message from orchestration to results
        message.isOrchestrating = false;
        message.showSkeletons = false;
        message.text = `tag: ${tag}`;
        message.totalMemories = response.data?.totalActiveMemories || 0;
        message.displayMode = this.displayMode;
        message.page = 1;
        message.hasMore = response.data?.hasMore || false;
        message.memories = [];
        message.summaryLoading = this.isSummaryModeActive() && (response.data?.memories?.length > 0);

        // Update final step with result count
        if (message.orchestrationSteps?.length) {
          const lastStep = message.orchestrationSteps[message.orchestrationSteps.length - 1];
          lastStep.text = `Found ${message.totalMemories} ${message.totalMemories === 1 ? 'memory' : 'memories'} with tag "${tag}"`;
          lastStep.status = 'complete';
        }

        this.messages = [...this.messages];
        this.shouldScrollToBottom = true;

        // Stream memories in one by one
        if (response.data?.memories?.length > 0) {
          await this.delay(300);
          this.streamMemories(response.data.memories, message, () => {
            this.messages = [...this.messages];
          });
        }

        // Fetch LLM summary if in summary mode
        if (this.isSummaryModeActive() && response.data?.memories?.length > 0) {
          this.fetchMemorySummaryStream(message, response.data.memories, `memories with tag: ${tag}`);
        }
      } else {
        throw new Error(response?.message || "Failed to fetch memories by tag");
      }
    } catch (error: any) {
      console.error("Fetch memories by tag error:", error);
      message.isOrchestrating = false;
      message.showSkeletons = false;
      message.messageType = 'error';
      message.text = `Failed to load memories: ${error.message || 'Unknown error'}`;
      this.messages = [...this.messages];
    }
  }

  // ============================================
  // LEGACY BETA METHODS (kept for backward compatibility)
  // ============================================

  private async listAllMemoriesInternal(): Promise<void> {
    this.loading = true;
    this.shouldScrollToBottom = true;
    await this.performSearch('');
  }

  private async listAllTagsInternal(): Promise<void> {
    this.loading = true;
    this.shouldScrollToBottom = true;

    try {
      const payload = {
        userKey: this.userKey
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) {
        throw new Error("Failed to sign payload");
      }

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.LIST_TAG,
        this.apiKey,
        signature
      );

      if (response && response.status === "Success") {
        const assistantMessage: ChatMessage = {
          type: 'assistant',
          text: 'all tags',
          tags: response.data?.notesTag || [],
          timestamp: new Date()
        };
        this.messages.push(assistantMessage);
        this.shouldScrollToBottom = true;
      } else {
        throw new Error(response?.message || "Failed to fetch tags");
      }
    } catch (error: any) {
      console.error("Tag search error:", error);
      this.appService.addErrorMessage("Failed to fetch tags: " + (error.message || "Unknown error"));

      const errorMessage: ChatMessage = {
        type: 'system',
        text: "Sorry, I encountered an error while fetching tags. Please try again.",
        messageType: 'error',
        timestamp: new Date()
      };
      this.messages.push(errorMessage);
      this.shouldScrollToBottom = true;
    } finally {
      this.loading = false;
    }
  }

  private async searchMemoriesByTagInternal(tag: string): Promise<void> {
    this.loading = true;
    this.shouldScrollToBottom = true;

    try {
      const payload = {
        userKey: this.userKey,
        tag: tag,
        page: 1,
        limit: MEMORY_CONFIG.PAGE_SIZE
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) {
        throw new Error("Failed to sign payload");
      }

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.BY_TAG,
        this.apiKey,
        signature
      );

      if (response && response.status === "Success") {
        const assistantMessage: ChatMessage = {
          type: 'assistant',
          text: `tag: ${tag}`,
          memories: [],
          totalMemories: response.data?.totalActiveMemories || 0,
          timestamp: new Date(),
          displayMode: this.displayMode,
          page: 1,
          hasMore: response.data?.hasMore || false,
          summaryLoading: this.isSummaryModeActive() && (response.data?.memories?.length > 0)
        };

        this.messages.push(assistantMessage);
        this.shouldScrollToBottom = true;

        if (response.data?.memories && response.data.memories.length > 0) {
          this.streamMemories(
            response.data.memories,
            assistantMessage,
            () => {
              this.messages = [...this.messages];
            }
          );
        }

        // Fetch LLM summary if in summary mode
        if (this.isSummaryModeActive() && response.data?.memories && response.data.memories.length > 0) {
          this.fetchMemorySummaryStream(assistantMessage, response.data.memories, `memories with tag: ${tag}`);
        }
      } else {
        throw new Error(response?.message || "Failed to fetch memories by tag");
      }
    } catch (error: any) {
      console.error("Tag search error:", error);
      this.appService.addErrorMessage("Failed to search memories by tag: " + (error.message || "Unknown error"));

      const errorMessage: ChatMessage = {
        type: 'system',
        text: "Sorry, I encountered an error while searching memories by tag. Please try again.",
        messageType: 'error',
        timestamp: new Date()
      };
      this.messages.push(errorMessage);
      this.shouldScrollToBottom = true;
    } finally {
      this.loading = false;
    }
  }

  // ============================================
  // STREAMING METHODS
  // ============================================

  private streamText(
    text: string,
    onUpdate: (partial: string) => void,
    onComplete?: () => void,
    customSpeed?: number
  ): void {
    if (!this.streamingEnabled) {
      onUpdate(text);
      if (onComplete) onComplete();
      return;
    }

    const speed = customSpeed !== undefined ? customSpeed : this.streamingSpeed;

    this.isStreaming = true;
    this.streamingContent = '';
    this.streamingIndex = 0;

    const animate = () => {
      if (this.streamingIndex < text.length) {
        this.streamingContent += text[this.streamingIndex];
        onUpdate(this.streamingContent);
        this.streamingIndex++;

        if (this.isStreaming) {
          this.animationFrameId = setTimeout(() => {
            requestAnimationFrame(animate);
          }, speed);
        }
      } else {
        this.isStreaming = false;
        if (onComplete) onComplete();
      }
    };

    requestAnimationFrame(animate);
  }

  private stopStreaming(): void {
    this.isStreaming = false;
    if (this.animationFrameId) {
      clearTimeout(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private completeStreamingImmediately(): void {
    this.stopStreaming();
  }

  private streamMemories(
    memories: any[],
    message: ChatMessage,
    onComplete?: () => void
  ): void {
    if (!this.streamingEnabled) {
      message.memories = memories;
      if (onComplete) onComplete();
      return;
    }

    message.memories = [];
    let currentIndex = 0;

    const addNext = () => {
      if (currentIndex < memories.length && this.streamingEnabled) {
        message.memories!.push(memories[currentIndex]);
        currentIndex++;
        this.shouldScrollToBottom = true;

        setTimeout(() => {
          requestAnimationFrame(addNext);
        }, STREAMING_PRESETS[this.currentPreset].itemDelay);
      } else {
        if (onComplete) onComplete();
      }
    };

    requestAnimationFrame(addNext);
  }

  // ============================================
  // SEARCH AND MESSAGE METHODS
  // ============================================

  goBack(): void {
    this.router.navigate(["/userlist"]);
  }

  /**
   * Send a typed message from the user
   * INTENT DISCOVERY: This is the ONLY entry point where intent discovery (beta mode) is used.
   * When user TYPES a query and submits, we analyze intent if beta mode is enabled.
   * All click actions (shortcuts, tags, menu items) bypass intent discovery and call actions directly.
   */
  sendMessage(): void {
    this.queryError = "";

    if (!this.query.trim()) {
      this.queryError = "Please enter a search query";
      return;
    }

    if (this.query.length > MEMORY_CONFIG.MAX_QUERY_LENGTH) {
      this.queryError = `Query too long (max ${MEMORY_CONFIG.MAX_QUERY_LENGTH} characters)`;
      return;
    }

    if (this.loading) {
      return;
    }

    const userMessage: ChatMessage = {
      type: 'user',
      text: this.query,
      timestamp: new Date()
    };

    this.messages.push(userMessage);
    this.loading = true;
    this.shouldScrollToBottom = true;

    const searchQuery = this.query;
    this.query = "";

    // Intent discovery ONLY for typed input when beta mode is enabled
    // Click actions (shortcuts, tags, menu items) always bypass this
    if (this.betaMode) {
      this.performBetaSearch(searchQuery);
    } else {
      this.searchSubject.next(searchQuery);
    }
  }

  /**
   * Execute shortcut commands directly without intent discovery
   * When user clicks a shortcut, they know exactly what they want - bypass beta intent analysis
   */
  executeShortcut(command: string): void {
    if (this.loading) return;

    const normalizedCommand = command.toLowerCase().trim();

    // Add user message to show what action was triggered
    const userMessage: ChatMessage = {
      type: 'user',
      text: command,
      timestamp: new Date()
    };
    this.messages.push(userMessage);
    this.shouldScrollToBottom = true;

    // Direct routing based on command - always bypass intent discovery for clicks
    if (normalizedCommand === 'list all memories' || normalizedCommand === 'show all memories') {
      this.loading = true;
      this.performSearch('');
    } else if (normalizedCommand === 'list all tags' || normalizedCommand === 'show all tags') {
      this.executeListTagsDirectly();
    } else {
      // For any other shortcut command, perform direct search
      this.loading = true;
      this.performSearch(command);
    }
  }

  /**
   * List all memories - direct action, always bypass intent discovery
   * When user clicks this, they explicitly want to see all memories
   */
  async listAllMemories(): Promise<void> {
    if (this.loading) {
      return;
    }

    this.showOptionsMenu = false;

    const userMessage: ChatMessage = {
      type: 'user',
      text: 'Show me all memories',
      timestamp: new Date()
    };

    this.messages.push(userMessage);
    this.loading = true;
    this.shouldScrollToBottom = true;

    // Always perform direct search - bypass intent discovery for explicit click actions
    this.showSystemMessage('Loading all memories...', 'info');
    await this.performSearch('');
  }

  private async performSearch(searchQuery: string): Promise<void> {
    try {
      const payload = {
        userKey: this.userKey,
        tokens: [],
        query: searchQuery,
        page: 1,
        limit: MEMORY_CONFIG.PAGE_SIZE,
        require_llm_summary: false
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) {
        throw new Error("Failed to sign payload");
      }

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.LIST,
        this.apiKey,
        signature
      );

      this.removeSystemMessages();

      if (response && response.status === "Success") {
        const assistantMessage: ChatMessage = {
          type: 'assistant',
          text: searchQuery || 'all memories',
          memories: [],
          totalMemories: response.data?.totalActiveMemories || 0,
          timestamp: new Date(),
          displayMode: this.displayMode,
          page: 1,
          hasMore: response.data?.hasMore || false,
          summaryLoading: this.isSummaryModeActive() && (response.data?.memories?.length > 0)
        };

        this.messages.push(assistantMessage);
        this.shouldScrollToBottom = true;

        // Stream success message
        const searchMessage = `Found ${assistantMessage.totalMemories} ${assistantMessage.totalMemories === 1 ? 'memory' : 'memories'
          }`;

        const streamingMsg: ChatMessage = {
          type: 'system',
          text: '',
          messageType: 'success',
          timestamp: new Date()
        };
        this.messages.push(streamingMsg);

        this.streamText(
          searchMessage,
          (partial) => {
            streamingMsg.text = partial;
            this.shouldScrollToBottom = true;
          },
          () => {
            setTimeout(() => {
              const idx = this.messages.indexOf(streamingMsg);
              if (idx > -1) this.messages.splice(idx, 1);
            }, 2000);
          }
        );

        // Stream memory results
        if (response.data?.memories && response.data.memories.length > 0) {
          setTimeout(() => {
            this.streamMemories(
              response.data.memories,
              assistantMessage,
              () => {
                this.messages = [...this.messages];
              }
            );
          }, 500);
        }

        // Get LLM summary if in summary mode
        if (this.isSummaryModeActive() && response.data?.memories && response.data.memories.length > 0) {
          this.fetchMemorySummaryStream(assistantMessage, response.data.memories, searchQuery);
        }

      } else {
        throw new Error(response?.message || "Search failed");
      }
    } catch (error: any) {
      console.error("Search error:", error);
      this.removeSystemMessages();

      this.appService.addErrorMessage("Failed to search memories: " + (error.message || "Unknown error"));

      const errorMessage: ChatMessage = {
        type: 'system',
        text: "Sorry, I encountered an error while searching memories. Please try again.",
        messageType: 'error',
        timestamp: new Date()
      };
      this.messages.push(errorMessage);
      this.shouldScrollToBottom = true;
    } finally {
      this.loading = false;
    }
  }

  private async fetchMemorySummary(message: ChatMessage, memories: any[], originalSentence: string): Promise<void> {
    try {
      const payload = {
        userKey: this.userKey,
        originalSentence: originalSentence,
        memories: memories,
        summaryPersona: this.summaryPersona
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) {
        throw new Error("Failed to sign summary payload");
      }

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        this.getSummaryEndpoint(),
        this.apiKey,
        signature
      );

      if (response && response.status === "Success") {
        const summaryData = response.data?.usageStats;

        let summary = '';
        if (typeof summaryData === 'string') {
          const summaryMatch = summaryData.match(/Personal Assistant Response: (.+)/s);
          if (summaryMatch) {
            summary = summaryMatch[1].trim();
          } else {
            summary = summaryData;
          }
        }

        message.memorySummary = summary;
        message.summaryLoading = false;
        this.shouldScrollToBottom = true;
        this.messages = [...this.messages];
      } else {
        throw new Error(response?.message || "Failed to generate summary");
      }
    } catch (error: any) {
      console.error("Summary generation error:", error);
      message.summaryLoading = false;
      message.memorySummary = "Failed to generate summary. Please try again.";
      this.messages = [...this.messages];
    }
  }

  // ============================================
  // SSE STREAMING METHODS FOR SUMMARY
  // ============================================

  private async fetchMemorySummaryStream(message: ChatMessage, memories: any[], originalSentence: string): Promise<void> {
    try {
      if (this.currentEventSource) {
        this.currentEventSource.close();
        this.currentEventSource = null;
      }

      const payload = {
        userKey: this.userKey,
        originalSentence: originalSentence,
        memories: memories,
        summaryPersona: this.summaryPersona
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) {
        throw new Error("Failed to sign summary payload");
      }

      const signature = new TextDecoder().decode(signatureData);

      // Use dynamic endpoint based on display mode
      const summaryEndpoint = this.getSummaryEndpoint();
      let fullUrl = '';
      if (this.appService.baseurl) {
        const baseUrl = this.appService.baseurl;
        fullUrl = `${baseUrl}/${summaryEndpoint}`;
      } else {
        const protocol = window.location.protocol;
        const host = window.location.host;
        fullUrl = `${protocol}//${host}/${summaryEndpoint}`;
      }

      console.log('[SSE] Streaming URL:', fullUrl);

      await this.streamSummaryWithFetch(fullUrl, payload, signature, message);

    } catch (error: any) {
      console.error("Summary streaming error:", error);
      message.summaryLoading = false;
      message.memorySummary = "Failed to generate summary. Please try again.";
      this.messages = [...this.messages];
    }
  }

  private async streamSummaryWithFetch(
    url: string,
    payload: any,
    signature: string,
    message: ChatMessage
  ): Promise<void> {
    console.log('[SSE] Starting stream request to:', url);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apiKey': this.apiKey,
          'signature': signature,
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify(payload)
      });

      console.log('[SSE] Response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      message.memorySummary = '';
      message.summaryLoading = true;
      this.shouldScrollToBottom = true;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('[SSE] Stream reader done');
          message.summaryLoading = false;
          this.messages = [...this.messages];
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();

            if (data === '[DONE]') {
              console.log('[SSE] Stream complete - [DONE] signal received');
              message.summaryLoading = false;
              this.messages = [...this.messages];
              continue;
            }

            try {
              const jsonData = JSON.parse(data);

              if (jsonData.done === true) {
                message.summaryLoading = false;
                this.messages = [...this.messages];
                continue;
              }

              if (jsonData.error) {
                throw new Error(jsonData.chunk || 'Stream error occurred');
              }

              let content = '';
              if (jsonData.chunk !== undefined && jsonData.chunk !== null && jsonData.chunk !== '') {
                content = jsonData.chunk;
              } else if (jsonData.content !== undefined && jsonData.content !== null && jsonData.content !== '') {
                content = jsonData.content;
              } else if (jsonData.text !== undefined && jsonData.text !== null && jsonData.text !== '') {
                content = jsonData.text;
              }

              if (content) {
                message.memorySummary += content;
                this.shouldScrollToBottom = true;
                this.messages = [...this.messages];
              }
            } catch (e) {
              if (data && data !== '' && data !== '[DONE]') {
                message.memorySummary += data;
                this.shouldScrollToBottom = true;
                this.messages = [...this.messages];
              }
            }
          }
        }
      }

    } catch (error: any) {
      console.error("Fetch streaming error:", error);
      message.summaryLoading = false;
      message.memorySummary = message.memorySummary || "Failed to generate summary. Please try again.";
      this.messages = [...this.messages];
    }
  }

  async loadMoreMemories(message: ChatMessage): Promise<void> {
    if (!message.hasMore || this.loading) return;

    this.loading = true;
    const nextPage = (message.page || 1) + 1;

    try {
      const payload = {
        userKey: this.userKey,
        tokens: [],
        query: message.text === 'all memories' ? '' : message.text,
        page: nextPage,
        limit: MEMORY_CONFIG.PAGE_SIZE,
        require_llm_summary: false
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) throw new Error("Failed to sign payload");

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.LIST,
        this.apiKey,
        signature
      );

      if (response && response.status === "Success") {
        const newMemories = response.data.memories;

        this.streamMemories(
          newMemories,
          { ...message, memories: [...(message.memories || [])] } as ChatMessage,
          () => {
            message.memories = [...(message.memories || []), ...newMemories];
            message.page = nextPage;
            message.hasMore = response.data.hasMore;
            this.messages = [...this.messages];
          }
        );
      } else {
        throw new Error(response?.message || "Failed to load more memories");
      }
    } catch (error: any) {
      console.error("Load more error:", error);
      this.appService.addErrorMessage("Failed to load more memories: " + (error.message || "Unknown error"));
    } finally {
      this.loading = false;
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcuts(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.focusSearchInput();
    }

    if (event.key === 'Escape') {
      this.showOptionsMenu = false;
      this.showDisplayModeMenu = false;
      if (this.isStreaming) {
        this.stopStreaming();
      }
    }

    if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && !this.showOptionsMenu && !this.showDisplayModeMenu) {
      event.preventDefault();
      this.navigateMessages(event.key === 'ArrowUp' ? -1 : 1);
    }
  }

  private focusSearchInput(): void {
    const inputElement = document.querySelector('.chat-input') as HTMLTextAreaElement;
    if (inputElement) {
      inputElement.focus();
    }
  }

  private navigateMessages(direction: number): void {
    const messageElements = document.querySelectorAll('.message[tabindex]');
    if (messageElements.length === 0) return;

    this.currentMessageIndex = Math.max(0, Math.min(messageElements.length - 1, this.currentMessageIndex + direction));
    (messageElements[this.currentMessageIndex] as HTMLElement).focus();
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        const element = this.messagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  @HostListener('scroll', ['$event'])
  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 150;
    const position = element.scrollTop + element.offsetHeight;
    const height = element.scrollHeight;

    this.autoScrollEnabled = (height - position) <= threshold;

    if (!this.autoScrollEnabled && this.isStreaming) {
      this.completeStreamingImmediately();
    }
  }

  highlightTokens(memory: string, tokens: string[]): string {
    let highlightedText = this.escapeHtml(memory);

    const urlRegex = /https?:\/\/[^\s]+/g;
    highlightedText = highlightedText.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="memory-link">${url}</a>`;
    });

    if (tokens && tokens.length > 0) {
      tokens.forEach((token) => {
        if (token && token.trim()) {
          const escapedToken = this.escapeRegex(token);
          const regex = new RegExp(
            `(?<!<[^>]*)(${escapedToken})(?![^<]*>)`,
            'gi'
          );
          highlightedText = highlightedText.replace(
            regex,
            '<mark class="highlight">$1</mark>'
          );
        }
      });
    }

    return highlightedText;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Get current active step text for LIAM Progress animation
   */
  getCurrentStepText(message: ChatMessage): string {
    if (!message.orchestrationSteps?.length) return 'Processing...';

    const activeStep = message.orchestrationSteps.find((s: OrchestrationStep) => s.status === 'active');
    if (activeStep) {
      return activeStep.text || 'Processing...';
    }

    // If no active step, show the last completed step or first pending
    const lastComplete = [...message.orchestrationSteps].reverse().find((s: OrchestrationStep) => s.status === 'complete');
    if (lastComplete) {
      return lastComplete.text || 'Processing...';
    }

    return message.orchestrationSteps[0]?.text || 'Processing...';
  }

  /**
   * Get description for current step in LIAM Progress animation
   */
  getCurrentStepDescription(message: ChatMessage): string {
    if (!message.orchestrationSteps?.length) return 'Please wait...';

    const activeStep = message.orchestrationSteps.find((s: OrchestrationStep) => s.status === 'active');
    if (activeStep) {
      // Generate contextual description based on step text
      const text = (activeStep.text || '').toLowerCase();

      if (text.includes('tag') || text.includes('loading')) {
        return 'Loading your personalized tags...';
      } else if (text.includes('understand') || text.includes('request')) {
        return 'Analyzing your question...';
      } else if (text.includes('intent') || text.includes('analyz')) {
        return 'Understanding what you\'re looking for...';
      } else if (text.includes('search') || text.includes('finding')) {
        return 'Searching through your memories...';
      } else if (text.includes('related') || text.includes('similar')) {
        return 'Finding related information...';
      } else if (text.includes('summar') || text.includes('generat')) {
        return 'Generating your summary...';
      } else if (text.includes('complete') || text.includes('finish')) {
        return 'Almost done...';
      }

      return 'LIAM is working on it...';
    }

    return 'Please wait...';
  }

  /**
   * Get all unique tokens from a message's memories
   * Used to highlight tokens in the AI summary
   */
  getAllTokensFromMemories(message: ChatMessage): string[] {
    if (!message.memories || message.memories.length === 0) {
      return [];
    }

    const tokenSet = new Set<string>();
    message.memories.forEach((memory: any) => {
      if (memory.tokens && Array.isArray(memory.tokens)) {
        memory.tokens.forEach((token: string) => {
          if (token && token.trim()) {
            tokenSet.add(token.trim());
          }
        });
      }
    });

    // Sort by length descending to match longer tokens first
    return Array.from(tokenSet).sort((a, b) => b.length - a.length);
  }

  /**
   * Render markdown to HTML with token highlighting for AI summary
   * Combines markdown rendering with clickable token links
   */
  renderMarkdownWithTokens(text: string, message: ChatMessage): string {
    if (!text) return '';

    // First render markdown
    let html = this.renderMarkdown(text);

    // Get all tokens from the message's memories
    const tokens = this.getAllTokensFromMemories(message);

    // Highlight tokens (similar to highlightTokens but works on HTML)
    // Using inline styles to override browser default yellow background for <mark>
    if (tokens && tokens.length > 0) {
      tokens.forEach((token) => {
        if (token && token.trim()) {
          const escapedToken = this.escapeRegex(token);
          // Match token but not if it's inside an HTML tag or already highlighted
          const regex = new RegExp(
            `(?<!<[^>]*|class="[^"]*)(\\b${escapedToken}\\b)(?![^<]*>|[^"]*")`,
            'gi'
          );
          html = html.replace(
            regex,
            '<mark class="highlight summary-token" style="background-color: transparent; background: none; color: #2563eb; font-weight: 600; cursor: pointer; border-bottom: 1px solid transparent;">$1</mark>'
          );
        }
      });
    }

    return html;
  }

  /**
   * Handle clicks on highlighted tokens within summary content
   * Similar to onMemoryContentClick but for the summary section
   */
  onSummaryTokenClick(event: Event, message: ChatMessage): void {
    const target = event.target as HTMLElement;

    if (target.classList.contains('highlight') || target.classList.contains('summary-token')) {
      const clickedToken = target.textContent || '';
      if (clickedToken && !this.loading) {
        // Add user message to show what was clicked
        const userMessage: ChatMessage = {
          type: 'user',
          text: clickedToken,
          timestamp: new Date()
        };
        this.messages.push(userMessage);
        this.loading = true;
        this.shouldScrollToBottom = true;

        // Direct search - bypass intent discovery for explicit click actions
        this.performSearch(clickedToken);
      }
    }
  }

  /**
   * Render markdown to HTML for streaming summary display
   * Handles: bold, italic, headers, lists, code, links, line breaks
   * Optimized for real-time streaming (called on every chunk)
   */
  renderMarkdown(text: string): string {
    if (!text) return '';

    // First escape HTML to prevent XSS
    let html = this.escapeHtml(text);

    // Convert markdown to HTML
    // Order matters - process in specific order to avoid conflicts

    // Headers (must come before bold to avoid conflicts with **)
    html = html.replace(/^### (.+)$/gm, '<h4 class="md-h3">$1</h4>');
    html = html.replace(/^## (.+)$/gm, '<h3 class="md-h2">$1</h3>');
    html = html.replace(/^# (.+)$/gm, '<h2 class="md-h1">$1</h2>');

    // Bold: **text** or __text__
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Italic: *text* or _text_ (but not inside words)
    html = html.replace(/(?<!\w)\*([^*]+?)\*(?!\w)/g, '<em>$1</em>');
    html = html.replace(/(?<!\w)_([^_]+?)_(?!\w)/g, '<em>$1</em>');

    // Inline code: `code`
    html = html.replace(/`([^`]+?)`/g, '<code class="md-inline-code">$1</code>');

    // Numbered lists: 1. item, 2. item, etc.
    // Note: We use a custom class and CSS counter to avoid double numbering
    html = html.replace(/^(\d+)\. (.+)$/gm, '<li class="md-list-item md-numbered" data-num="$1">$2</li>');

    // Bullet lists: - item or * item
    html = html.replace(/^[-*] (.+)$/gm, '<li class="md-list-item md-bullet">$1</li>');

    // Wrap consecutive list items in ul/ol (simplified approach)
    // Using <div> with role="list" for numbered to avoid browser auto-numbering conflicts
    html = html.replace(/(<li class="md-list-item md-numbered"[^>]*>.*?<\/li>\n?)+/g, '<div class="md-list md-numbered-list" role="list">$&</div>');
    html = html.replace(/(<li class="md-list-item md-bullet">.*?<\/li>\n?)+/g, '<ul class="md-list md-bullet-list">$&</ul>');

    // Links: [text](url)
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="md-link">$1</a>');

    // Line breaks: convert double newlines to paragraphs, single to <br>
    html = html.replace(/\n\n/g, '</p><p class="md-paragraph">');
    html = html.replace(/\n/g, '<br>');

    // Wrap in paragraph if not empty
    if (html.trim()) {
      html = '<p class="md-paragraph">' + html + '</p>';
    }

    // Clean up empty paragraphs
    html = html.replace(/<p class="md-paragraph"><\/p>/g, '');
    html = html.replace(/<p class="md-paragraph">(<[hou]l)/g, '$1');
    html = html.replace(/(<\/[hou]l>)<\/p>/g, '$1');

    return html;
  }

  /**
   * Handle clicks on highlighted tokens within memory content
   * Direct action - always bypass intent discovery when user clicks a token link
   */
  onMemoryContentClick(event: Event, tokens: string[]): void {
    const target = event.target as HTMLElement;

    if (target.classList.contains('highlight')) {
      const clickedToken = target.textContent || '';
      if (clickedToken && !this.loading) {
        // Add user message to show what was clicked
        const userMessage: ChatMessage = {
          type: 'user',
          text: clickedToken,
          timestamp: new Date()
        };
        this.messages.push(userMessage);
        this.loading = true;
        this.shouldScrollToBottom = true;

        // Direct search - bypass intent discovery for explicit click actions
        this.performSearch(clickedToken);
      }
    }
  }

  /**
   * Search memories by tag - direct action, always bypass intent discovery
   * When user clicks a tag, they explicitly want memories with that tag
   */
  async searchMemoriesByTag(tag: string): Promise<void> {
    if (this.loading) {
      return;
    }

    const userMessage: ChatMessage = {
      type: 'user',
      text: `Show memories with tag: ${tag}`,
      timestamp: new Date()
    };

    this.messages.push(userMessage);
    this.loading = true;
    this.shouldScrollToBottom = true;

    // Always perform direct tag search - bypass intent discovery for explicit click actions
    try {
      const payload = {
        userKey: this.userKey,
        tag: tag,
        page: 1,
        limit: MEMORY_CONFIG.PAGE_SIZE
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) {
        throw new Error("Failed to sign payload");
      }

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.BY_TAG,
        this.apiKey,
        signature
      );

      if (response && response.status === "Success") {
        const assistantMessage: ChatMessage = {
          type: 'assistant',
          text: `tag: ${tag}`,
          memories: [],
          totalMemories: response.data?.totalActiveMemories || 0,
          timestamp: new Date(),
          displayMode: this.displayMode,
          page: 1,
          hasMore: response.data?.hasMore || false,
          summaryLoading: this.isSummaryModeActive() && (response.data?.memories?.length > 0)
        };

        this.messages.push(assistantMessage);
        this.shouldScrollToBottom = true;

        if (response.data?.memories && response.data.memories.length > 0) {
          this.streamMemories(
            response.data.memories,
            assistantMessage,
            () => {
              this.messages = [...this.messages];
            }
          );
        }

        // Fetch LLM summary if in summary mode
        if (this.isSummaryModeActive() && response.data?.memories && response.data.memories.length > 0) {
          this.fetchMemorySummaryStream(assistantMessage, response.data.memories, `memories with tag: ${tag}`);
        }
      } else {
        throw new Error(response?.message || "Failed to fetch memories by tag");
      }
    } catch (error: any) {
      console.error("Tag search error:", error);
      this.appService.addErrorMessage("Failed to search memories by tag: " + (error.message || "Unknown error"));

      const errorMessage: ChatMessage = {
        type: 'system',
        text: "Sorry, I encountered an error while searching memories by tag. Please try again.",
        messageType: 'error',
        timestamp: new Date()
      };
      this.messages.push(errorMessage);
      this.shouldScrollToBottom = true;
    } finally {
      this.loading = false;
    }
  }

  /**
   * List all tags - direct action, always bypass intent discovery
   * When user clicks this, they explicitly want to see all tags
   */
  async listAllTags(): Promise<void> {
    if (this.loading) {
      return;
    }

    const userMessage: ChatMessage = {
      type: 'user',
      text: 'Show me all tags',
      timestamp: new Date()
    };

    this.messages.push(userMessage);
    this.loading = true;
    this.shouldScrollToBottom = true;

    // Always perform direct tag listing - bypass intent discovery for explicit click actions
    await this.executeListTagsDirectly();
  }

  /**
   * Execute list tags directly without intent discovery
   * Extracted for reuse by both listAllTags() and executeShortcut()
   */
  private async executeListTagsDirectly(): Promise<void> {
    this.loading = true;

    try {
      const payload = {
        userKey: this.userKey
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) {
        throw new Error("Failed to sign payload");
      }

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.LIST_TAG,
        this.apiKey,
        signature
      );

      if (response && response.status === "Success") {
        const assistantMessage: ChatMessage = {
          type: 'assistant',
          text: 'all tags',
          tags: response.data?.notesTag || [],
          timestamp: new Date()
        };
        this.messages.push(assistantMessage);
        this.shouldScrollToBottom = true;
      } else {
        throw new Error(response?.message || "Failed to fetch tags");
      }
    } catch (error: any) {
      console.error("List tags error:", error);
      this.appService.addErrorMessage("Failed to list tags: " + (error.message || "Unknown error"));

      const errorMessage: ChatMessage = {
        type: 'system',
        text: "Sorry, I encountered an error while fetching tags. Please try again.",
        messageType: 'error',
        timestamp: new Date()
      };
      this.messages.push(errorMessage);
      this.shouldScrollToBottom = true;
    } finally {
      this.loading = false;
    }
  }

  // ============================================
  // SYSTEM MESSAGE METHODS
  // ============================================

  showSystemMessage(text: string, type: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    const systemMessage: ChatMessage = {
      type: 'system',
      text: text,
      messageType: type,
      timestamp: new Date()
    };
    this.messages.push(systemMessage);
    this.shouldScrollToBottom = true;
  }

  removeSystemMessages(): void {
    this.messages = this.messages.filter(m => m.type !== 'system');
  }

  // ============================================
  // EXPORT AND UTILITY METHODS
  // ============================================

  exportChat(): void {
    const exportData = {
      exportDate: new Date().toISOString(),
      messages: this.messages.map(m => ({
        type: m.type,
        text: m.text,
        timestamp: m.timestamp,
        memoriesCount: m.memories?.length || 0,
        tagsCount: m.tags?.length || 0
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `chat-history-${new Date().toISOString()}.json`;
    link.click();
    window.URL.revokeObjectURL(url);

    this.showSystemMessage('Chat exported successfully!', 'success');
    this.showOptionsMenu = false;
  }

  clearChat(): void {
    if (confirm('Are you sure you want to clear the chat history? This action cannot be undone.')) {
      this.messages = [];
      this.showOptionsMenu = false;
      this.showSystemMessage('Chat history cleared', 'info');
    }
  }

  private showWelcomeMessageIfNeeded(): void {
    const showWelcome = this.appService.get('xdbc.showWelcomeMessage');

    if (showWelcome === 'true') {
      this.appService.remove('xdbc.showWelcomeMessage');

      const userName = this.appService.get('xdbc.emailId') || 'there';
      const email = userName.includes('@') ? userName.split('@')[0] : userName;

      const welcomeText = `Hello ${email}! 👋

  Welcome to your personal memory assistant. I'm here to help you manage and search through your memories.

  Try one of these quick actions:`;

      const welcomeMessage: ChatMessage = {
        type: 'assistant',
        text: '',
        messageType: 'info',
        timestamp: new Date(),
        showShortcuts: true
      };

      this.messages.push(welcomeMessage);
      this.shouldScrollToBottom = true;

      this.streamText(
        welcomeText,
        (partial) => {
          welcomeMessage.text = partial;
          this.shouldScrollToBottom = true;
        },
        () => {
          console.log('Welcome message streaming complete');
        }
      );
    }
  }

  // ============================================
  // QUICK ACCESS TAGS METHODS
  // ============================================

  async loadQuickAccessTags(): Promise<void> {
    if (!this.userKey) return;

    this.quickAccessTagsLoading = true;

    try {
      const payload = {
        userKey: this.userKey
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) {
        throw new Error("Failed to sign payload");
      }

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.LIST_TAG,
        this.apiKey,
        signature
      );

      if (response && response.status === "Success") {
        this.quickAccessTags = response.data?.notesTag || [];
      }
    } catch (error: any) {
      console.error("Failed to load quick access tags:", error);
    } finally {
      this.quickAccessTagsLoading = false;
    }
  }

  refreshQuickAccessTags(): void {
    this.loadQuickAccessTags();
  }

  toggleQuickAccessTags(): void {
    this.showQuickAccessTags = !this.showQuickAccessTags;
  }

  // ============================================
  // SENSITIVE MEMORY MANAGEMENT METHODS
  // ============================================

  /**
   * Check if a memory has sensitive data (HIGH or CRITICAL sensitivity)
   */
  isSensitiveMemory(memory: any): boolean {
    const sensitivity = memory?.sensitivity?.toUpperCase();
    return sensitivity === 'HIGH' || sensitivity === 'CRITICAL';
  }

  /**
   * Check if a sensitive memory has been revealed by the user
   */
  isMemoryRevealed(memory: any): boolean {
    const memoryId = this.getMemoryIdentifier(memory);
    return this.revealedSensitiveMemories.has(memoryId);
  }

  /**
   * Toggle the revealed state of a sensitive memory
   */
  toggleMemoryReveal(memory: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const memoryId = this.getMemoryIdentifier(memory);
    if (this.revealedSensitiveMemories.has(memoryId)) {
      this.revealedSensitiveMemories.delete(memoryId);
    } else {
      this.revealedSensitiveMemories.add(memoryId);
    }
  }

  /**
   * Get masked display text for sensitive memories
   * Returns dots/bullets to hide the actual content
   */
  getMaskedMemoryContent(memory: any): string {
    const text = memory?.memory || '';
    // Create a masked version - show approximate length but hide content
    const words = text.split(/\s+/);
    const maskedWords = words.map((word: string) => '•'.repeat(Math.min(word.length, 8)));
    return maskedWords.join(' ');
  }

  /**
   * Get the display content for a memory - masked or clear based on sensitivity and reveal state
   */
  getMemoryDisplayContent(memory: any): string {
    if (this.isSensitiveMemory(memory) && !this.isMemoryRevealed(memory)) {
      return this.getMaskedMemoryContent(memory);
    }
    return memory?.memory || '';
  }

  /**
   * Get sensitivity level label for display
   */
  getSensitivityLabel(memory: any): string {
    const sensitivity = memory?.sensitivity?.toUpperCase();
    if (sensitivity === 'CRITICAL') return 'Critical';
    if (sensitivity === 'HIGH') return 'Sensitive';
    return '';
  }

  /**
   * Get sensitivity icon class
   */
  getSensitivityIcon(memory: any): string {
    const sensitivity = memory?.sensitivity?.toUpperCase();
    if (sensitivity === 'CRITICAL') return 'fa-shield-halved';
    if (sensitivity === 'HIGH') return 'fa-lock';
    return '';
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Toggle the collapsed state of memory results
   * Collapsed by default (isCollapsed !== false means collapsed)
   */
  toggleMemoryCollapse(message: ChatMessage): void {
    // If isCollapsed is undefined or true, expand it (set to false)
    // If isCollapsed is false, collapse it (set to true)
    message.isCollapsed = message.isCollapsed === false ? true : false;
  }

  toggleOptionsMenu(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showOptionsMenu = !this.showOptionsMenu;
  }

  openPerplexityMcpConfig(): void {
    this.showOptionsMenu = false;

    // Get base URL from environment
    const baseUrl = environment.baseUrl + "/api";

    const dialogRef = this.dialog.open(McpConfigDialogComponent, {
      width: '500px',
      data: {
        userKey: this.userKey,
        apiKey: this.apiKey,
        baseUrl: baseUrl,
        workspaceName: this.workspaceName
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'downloaded') {
        this.appService.addSuccessMessage("MCP configuration downloaded successfully");
      }
    });
  }

  getUserInitial(): string {
    const email = this.appService.get('xdbc.emailId') || '';
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return 'U';
  }

  getChromeUserEmail(): string {
    return this.appService.get('xdbc.emailId') || '';
  }

  // ============================================
  // TAG EDITING METHODS
  // ============================================

  isEditingTag(tag: string): boolean {
    return this.editingTag === tag;
  }

  startEditingTag(tag: string): void {
    if (this.loading) return;

    this.editingTag = tag;
    this.editingTagNewName = tag;

    setTimeout(() => {
      const input = document.querySelector('.tag-edit-input') as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  cancelTagEdit(): void {
    this.editingTag = null;
    this.editingTagNewName = '';
  }

  async saveTagEdit(currentTag: string): Promise<void> {
    if (this.loading) return;

    const newTag = this.editingTagNewName.trim();

    if (!newTag) {
      const errorMessage: ChatMessage = {
        type: 'system',
        text: 'Tag name cannot be empty',
        messageType: 'error',
        timestamp: new Date()
      };
      this.messages.push(errorMessage);
      this.shouldScrollToBottom = true;
      return;
    }

    if (newTag === currentTag) {
      this.cancelTagEdit();
      return;
    }

    this.loading = true;
    this.cancelTagEdit();

    try {
      const payload = {
        userKey: this.userKey,
        currentTag: currentTag,
        updatedTag: newTag
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) throw new Error("Failed to sign payload");

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.CHANGE_TAG,
        this.apiKey,
        signature
      );

      if (response && response.status === "Success") {
        this.messages.forEach(message => {
          if (message.tags) {
            const index = message.tags.indexOf(currentTag);
            if (index !== -1) {
              message.tags[index] = newTag;
            }
          }
        });

        const qatIndex = this.quickAccessTags.indexOf(currentTag);
        if (qatIndex !== -1) {
          this.quickAccessTags[qatIndex] = newTag;
        }

        const updatedCount = response.data?.updatedCount || 0;
        const successMessage: ChatMessage = {
          type: 'assistant',
          text: `✓ Tag renamed successfully from "${currentTag}" to "${newTag}". ${updatedCount} ${updatedCount === 1 ? 'memory' : 'memories'} updated.`,
          messageType: 'success',
          timestamp: new Date()
        };
        this.messages.push(successMessage);
        this.shouldScrollToBottom = true;
      } else {
        throw new Error(response?.message || "Failed to update tag");
      }
    } catch (error: any) {
      console.error("Tag update error:", error);

      const errorMessage: ChatMessage = {
        type: 'assistant',
        text: `✗ Failed to update tag "${currentTag}": ${error.message || "Unknown error"}. Please try again.`,
        messageType: 'error',
        timestamp: new Date()
      };
      this.messages.push(errorMessage);
      this.shouldScrollToBottom = true;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Start polling for memory queue status
   */
  private startMemoryStatusPolling(): void {
    this.stopMemoryStatusPolling(); // Clear any existing interval
    this.memoryStatusInterval = setInterval(() => {
      this.loadMemoryQueueStatus();
    }, this.memoryStatusPollingMs);
  }

  /**
   * Stop polling for memory queue status
   */
  private stopMemoryStatusPolling(): void {
    if (this.memoryStatusInterval) {
      clearInterval(this.memoryStatusInterval);
      this.memoryStatusInterval = null;
    }
  }

  /**
   * Refresh memory status manually (called from UI)
   */
  refreshMemoryStatus(): void {
    this.loadMemoryQueueStatus();
  }

  /**
   * Load memory queue status from API
   */
  async loadMemoryQueueStatus(): Promise<void> {
    if (!this.userKey) return;

    try {
      const payload = {
        userKey: this.userKey
      };

      const signatureData = await this.appService.wssign(payload, this.workspaceName);
      if (!signatureData) {
        console.warn("Failed to sign payload for memory status");
        return;
      }

      const signature = new TextDecoder().decode(signatureData);
      const response = await this.appService.wsPostRequestWithHeaders(
        payload,
        MEMORY_CONFIG.API_ENDPOINTS.MEMORY_STATUS,
        this.apiKey,
        signature
      );

      if (response && response.status === "Success" && response.data?.queueStatus) {
        this.memoryQueueStatus = response.data.queueStatus;

        // Calculate total items in NEW or PROCESSING state
        const breakdown = response.data.queueStatus.breakdown;
        this.memoryQueueCount = (breakdown?.NEW || 0) + (breakdown?.PROCESSING || 0);

        // If count dropped to 0 and was previously > 0, refresh the tag list
        // This indicates processing completed
        if (this.memoryQueueCount === 0 && this.memoryQueueStatus?.pending === 0) {
          // Optionally refresh tags when processing completes
          // this.refreshQuickAccessTags();
        }
      }
    } catch (error: any) {
      console.error("Failed to load memory queue status:", error);
      // Don't show error to user as this is background polling
    }
  }

  /**
   * Get a human-readable status text
   */
  getMemoryStatusText(): string {
    if (!this.memoryQueueStatus) return '';

    const breakdown = this.memoryQueueStatus.breakdown;
    const newCount = breakdown?.NEW || 0;
    const processingCount = breakdown?.PROCESSING || 0;

    if (newCount > 0 && processingCount > 0) {
      return `${newCount} queued, ${processingCount} processing`;
    } else if (newCount > 0) {
      return `${newCount} in queue`;
    } else if (processingCount > 0) {
      return `${processingCount} processing`;
    }
    return '';
  }

  // ============================================
  // IMAGE HANDLING METHODS
  // ============================================

  handleImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.classList.add('error');
    imgElement.alt = 'Failed to load image';
    console.error('Image failed to load:', imgElement.src);
  }
}